import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { assignmentPlaces, placeLabel } from '../utils/assignmentPlaces';
import { placeholdersEnabled, withPlaceholderSetting, PLACEHOLDER_EXAMPLES } from '../utils/blankPlaceholders';
import SearchableSelect from '../components/SearchableSelect';
import TagInput from '../components/TagInput';
import MultiSelect from '../components/MultiSelect';
import SetColumnEditor from '../components/SetColumnEditor';
import { parseMultiColumnPaste } from '../utils/multiColumnList';
import { readFileHeaderRow, bestFieldMatch, confidenceLabel } from '../utils/fileFieldMatch';
import { buildStatusGroupMap, buildProgramOptionGroups, closedProgramIdSet, buildComponentOptionGroups, buildGoalOptionGroups } from '../utils/statusGroups';
import { buildMatchFieldOptions } from '../utils/targetingListFields';
import { conversionOptions, conversionToValue, valueToConversion, showsDateConversion } from '../utils/dateConversions';

const API = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Check metadata
// ---------------------------------------------------------------------------
const CHECK_META = {
  // F2 — the only F2 check that's program/component-configurable (never goal)
  required_fields:  { label: 'Required Fields',       description: "Set each field to Required (Client) — hard reject if blank; Required (Supplier) — accepted but flagged Needs Review if blank; or Optional — shown to the supplier, never flagged. Configurable at Program or Component only; falls back to the default 6 fields (First/Last Name, Email, Title, Company, Country) as Required (Client) when not configured.", source: 'required_fields' },
  // F4
  geo:              { label: 'Geo (State / Country)', description: 'Flag leads whose state or country is not in any Geography targeting list.',        source: 'targeting_lists', sourceKey: 'geo',      listType: 'Geography' },
  segment:          { label: 'Segment',               description: 'Flag leads whose segment is not in any Segment targeting list.',                   source: 'targeting_lists', sourceKey: 'segment',  listType: 'Segment'   },
  industry:         { label: 'Industry',              description: 'Flag leads whose industry is not in any Industry targeting list.',                 source: 'targeting_lists', sourceKey: 'industry', listType: 'Industry'  },
  employee_size:    { label: 'Employee Size',         description: "Flag leads whose size doesn't fall within an allowed band. Uses 'Actual Co. Size' (numeric) when present; falls back to 'Number of Employees' band label.", source: 'employee_size', listConnectable: [{ listType: 'Employee Size Bands', columns: null }] },
  tal:              { label: 'Target Account List',   description: 'Flag leads whose company is not in any Account List assigned to this goal.',        source: 'targeting_lists', sourceKey: 'tal', listType: 'Account List' },
  set_check:        { label: 'Set Check',             description: 'Flag leads that do not match every configured column of any entry in an assigned Set (e.g. company name AND zip code must both match the same entry) — for disambiguating common names, like the Uber Restaurants TAL.', source: 'targeting_lists', sourceKey: 'set_check', listType: 'Set Check' },
  // F5
  phone:            { label: 'Phone',                 description: 'Flag leads with a missing or suspiciously short phone number.',                   source: 'none' },
  title_keywords:   { label: 'Title Keywords',        description: 'Flag leads whose job title does not match approved keywords, or matches a rejected keyword. Assigns each lead a Primary / Secondary / Reject category.', source: 'keywords', listConnectable: [{ listType: 'Title Keywords', columns: ['keyword', 'category', 'match_type'] }] },
  asset_alignment:  { label: 'Asset Alignment',       description: 'Flag leads whose downloaded asset does not match an allowed asset title.',        source: 'assets', listConnectable: [{ listType: 'Asset Alignment', columns: null }] },
  consent:          { label: 'Consent',               description: 'Reject leads missing privacy_policy_checked__c or single_opt_in__c acceptance.', source: 'none' },
  name_email_match: { label: 'Name / Email Match',    description: 'Flag leads where neither first nor last name appears in the email local-part (catches fake names).', source: 'none' },
  address:          { label: 'Address Format',        description: 'Flag leads where address is filled in but does not begin with a street number.',  source: 'none' },
  domain_cap:       { label: 'Domain Cap',             description: 'Flag leads whose email domain already has the maximum number of accepted leads in this goal.', source: 'domain_cap' },
  carryover_list:   { label: 'Carryover List',          description: 'Flag leads that appeared in a prior campaign phase — by email address and/or by domain count offsets.', source: 'carryover_list', listConnectable: [
    { listType: 'Carryover Emails', columns: null, subLabel: 'Carryover emails' },
    { listType: 'Carryover Domain Counts', columns: ['domain', 'count'], subLabel: 'Carryover domain counts' },
  ] },
};
// checkName -> targeting_summary bucket key for checks whose list-connect
// entries don't share the check's own name (carryover_list's two sub-types
// both live under the single "carryover" bucket — see backend qa_rules.py).
const LIST_CONNECT_BUCKET = {
  employee_size: 'employee_size',
  asset_alignment: 'asset_alignment',
  title_keywords: 'title_keywords',
  carryover_list: 'carryover',
};
const F2_CONFIGURABLE_CHECKS = ['required_fields'];
const F4_CHECKS = ['geo', 'segment', 'industry', 'employee_size', 'tal', 'set_check', 'carryover_list'];
const F5_CHECKS = ['phone', 'title_keywords', 'asset_alignment', 'consent', 'name_email_match', 'address', 'domain_cap'];

const LEVEL_LABELS = { program: 'Program', component: 'Component', goal: 'Goal' };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#555', display: 'block', marginBottom: '4px' };

const pillBadge = (color, bg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', color, background: bg, whiteSpace: 'nowrap' });

// ---------------------------------------------------------------------------
// Single check row
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Match-field defaults for each list-based check type
// ---------------------------------------------------------------------------
const LIST_CHECK_DEFAULTS = {
  geo:         { defaultMatchField: 'state',        matchType: 'case_insensitive' },
  segment:     { defaultMatchField: 'segment',      matchType: 'case_insensitive' },
  industry:    { defaultMatchField: 'industry',     matchType: 'case_insensitive' },
  tal:         { defaultMatchField: 'company_name', matchType: 'case_insensitive' },
  suppression: { defaultMatchField: 'email',        matchType: 'case_insensitive' },
};

// Same idea as LIST_CHECK_DEFAULTS, but keyed by listType instead of checkName
// — needed since carryover_list connects to two different list types (Carryover
// Emails / Carryover Domain Counts) under one check. match_field isn't actually
// consulted by these checks' logic (unlike geo/segment/etc.) — just a sane,
// stable value for the TargetingList row.
const LIST_TYPE_DEFAULTS = {
  'Employee Size Bands':      { defaultMatchField: 'number_of_employees', matchType: 'exact' },
  'Asset Alignment':          { defaultMatchField: 'asset_downloaded',    matchType: 'case_insensitive' },
  'Title Keywords':           { defaultMatchField: 'title',               matchType: 'exact' },
  'Carryover Emails':         { defaultMatchField: 'email',               matchType: 'case_insensitive' },
  'Carryover Domain Counts':  { defaultMatchField: 'company_domain',      matchType: 'case_insensitive' },
};

// ---------------------------------------------------------------------------
// ListManagePanel — assign/create/unassign targeting lists for a check
// ---------------------------------------------------------------------------
// Where a NeTrA-made list came from (TargetingList.source_json, migration
// 0120) — a carryover list generated from a prior program, or an
// auto-learned titles list that grows as leads are decided.
function sourceBadge(source) {
  if (!source) return '';
  if (source.kind === 'auto_accepted_titles' || source.kind === 'auto_rejected_titles') return ' · auto-learned';
  return ' · generated';
}
function sourceTooltip(source) {
  if (!source) return undefined;
  if (source.kind === 'auto_accepted_titles') return 'Grows automatically: titles of leads that reach DemandArc or Client Accepted';
  if (source.kind === 'auto_rejected_titles') return 'Grows automatically: titles of leads rejected with a title flag (QA or client feedback)';
  const when = source.generated_at ? new Date(source.generated_at + 'Z').toLocaleString('en-US', { timeZone: 'America/New_York' }) : '';
  return `Generated from ${(source.program_names || []).join(' + ')} · ${(source.statuses || []).join(', ')} · ${source.lead_count ?? '?'} leads · ${when}`;
}

function ListManagePanel({ checkName, listType, columns, assignedLists, level, entityId, onChanged, allLeadFields, onOverlayChange }) {
  const [connectOpen, setConnectOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [availableLists, setAvailableLists] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');
  const [newListName, setNewListName] = useState('');
  const [newListValues, setNewListValues] = useState('');
  const [geoField, setGeoField] = useState('state');
  const [opSaving, setOpSaving] = useState(false);
  const [opMsg, setOpMsg] = useState(null);
  const [unassigning, setUnassigning] = useState(null); // list id being unassigned
  const [refreshing, setRefreshing] = useState(null); // generated carryover list id being refreshed

  // Generated Carryover Domain Counts lists only (tl.source, migration 0120) —
  // recompute from the same prior program(s)/statuses, e.g. after the prior
  // program kept delivering. Assignments and the rule's max are untouched.
  const refreshGenerated = (tl) => {
    if (!window.confirm(`Recount "${tl.list_name}" from ${(tl.source.program_names || []).join(' + ') || 'its source program'}? This replaces the list's current counts.`)) return;
    setRefreshing(tl.id);
    setOpMsg(null);
    axios.post(`${API}/targeting-lists/${tl.id}/refresh-carryover`)
      .then(r => {
        const prev = r.data.previous_domain_count;
        setOpMsg({ ok: true, text: `✓ Refreshed — ${r.data.domain_count.toLocaleString()} domains${prev != null ? ` (was ${prev.toLocaleString()})` : ''}.` });
        onChanged && onChanged();
      })
      .catch(e => setOpMsg({ ok: false, text: e.response?.data?.detail || 'Refresh failed' }))
      .finally(() => setRefreshing(null));
  };

  // Set Check only — per-assigned-list condition editor ("only apply THIS
  // list when [field] is one of [...]"), independent of the rule-level
  // condition CheckRow renders above this panel. Explicit Save/Cancel, not
  // auto-save-on-edit — the rule-level control's debounced auto-save caused
  // two separate dropdown-reset races this session; editing a list's
  // condition is rare enough that instant auto-save isn't worth that risk
  // here. editingConditionId is the tl.id currently open for editing (one
  // at a time), seeded from that pill's own tl.condition on open.
  const [editingConditionId, setEditingConditionId] = useState(null);
  // Tell the enclosing CheckRow card when one of this panel's pickers is
  // open, so it can lift itself above the cards below — see CheckRow's
  // overlayOpen for why the card has to be the one that moves.
  const anyPickerOpen = connectOpen || createOpen || editingConditionId != null;
  useEffect(() => {
    if (!onOverlayChange) return undefined;
    onOverlayChange(listType, anyPickerOpen);
    return () => onOverlayChange(listType, false);
  }, [onOverlayChange, listType, anyPickerOpen]);
  const [condField, setCondField] = useState('');
  const [condValues, setCondValues] = useState([]);
  const [condElseEnabled, setCondElseEnabled] = useState(false);
  const [condElseFields, setCondElseFields] = useState([]);
  const [condSaving, setCondSaving] = useState(false);
  const [condMsg, setCondMsg] = useState(null);

  // Set Check only — its columns are PM-defined per-list, not a fixed set
  // per type like `columns` above (Title Keywords/Carryover Domain Counts).
  const isSetCheck = listType === 'Set Check';
  const [setColumns, setSetColumns] = useState([{ name: '', lead_field: '', match_type: 'exact' }]);
  const effectiveColumns = isSetCheck
    ? setColumns.filter(c => c.name.trim() && c.lead_field).map(c => c.name.trim())
    : columns;

  const canManage = !!level && !!entityId;

  const loadAvailable = () => {
    setLoadingAvail(true);
    axios.get(`${API}/targeting-lists/`, { params: { list_type: listType } })
      .then(r => setAvailableLists(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingAvail(false));
  };

  const openConnect = () => {
    setConnectOpen(true); setCreateOpen(false);
    loadAvailable();
  };

  const openCreate = () => {
    setCreateOpen(true); setConnectOpen(false);
    setNewListName(''); setNewListValues(''); setOpMsg(null);
  };

  // Assign an existing list to the entity
  const assignList = async (listId) => {
    setOpSaving(true);
    setOpMsg(null);
    try {
      if (level === 'goal') {
        await axios.post(`${API}/targeting-lists/${listId}/assignments`, { goal_ids: [Number(entityId)] });
      } else if (level === 'component') {
        await axios.post(`${API}/targeting-lists/${listId}/assign-component`, { component_id: Number(entityId) });
      } else {
        await axios.post(`${API}/targeting-lists/${listId}/assign-program`, { program_id: Number(entityId) });
      }
      setConnectOpen(false); setSelectedListId('');
      onChanged();
    } catch (e) {
      setOpMsg({ ok: false, text: e.response?.data?.detail || 'Assign failed' });
    }
    setOpSaving(false);
  };

  // Create a new list, add items, then assign
  const createAndAssign = async () => {
    const name = newListName.trim();
    if (!name) return;
    setOpSaving(true);
    setOpMsg(null);
    const defaults = LIST_CHECK_DEFAULTS[checkName] || LIST_TYPE_DEFAULTS[listType] || {};
    let matchField = checkName === 'geo' ? geoField : (defaults.defaultMatchField || checkName);
    let columnConfig;
    if (isSetCheck) {
      const validCols = setColumns.filter(c => c.name.trim() && c.lead_field);
      if (!validCols.length) {
        setOpMsg({ ok: false, text: 'Define at least one column with a name and a matching Lead field.' });
        setOpSaving(false);
        return;
      }
      columnConfig = validCols;
      matchField = effectiveColumns[0];   // nominal — check_set() only reads column_config
    }
    try {
      // 1. Create list
      const res = await axios.post(`${API}/targeting-lists/`, {
        list_name: name,
        list_type: listType,
        match_field: matchField,
        match_type: defaults.matchType || 'case_insensitive',
        columns: effectiveColumns || undefined,
        column_config: columnConfig,
      });
      const newId = res.data.id;

      // 2. Add items
      let itemCount = 0;
      if (effectiveColumns) {
        const rows = parseMultiColumnPaste(newListValues, effectiveColumns);
        if (rows.length) {
          await axios.post(`${API}/targeting-lists/${newId}/items`, { rows });
        }
        itemCount = rows.length;
      } else {
        const values = newListValues.split('\n').map(v => v.trim()).filter(Boolean);
        if (values.length) {
          await axios.post(`${API}/targeting-lists/${newId}/items`, { values });
        }
        itemCount = values.length;
      }

      // 3. Assign to entity
      if (level === 'goal') {
        await axios.post(`${API}/targeting-lists/${newId}/assignments`, { goal_ids: [Number(entityId)] });
      } else if (level === 'component') {
        await axios.post(`${API}/targeting-lists/${newId}/assign-component`, { component_id: Number(entityId) });
      } else {
        await axios.post(`${API}/targeting-lists/${newId}/assign-program`, { program_id: Number(entityId) });
      }

      setOpMsg({ ok: true, text: `Created "${name}" with ${itemCount} item${itemCount !== 1 ? 's' : ''} and assigned.` });
      setNewListName(''); setNewListValues(''); setCreateOpen(false);
      if (isSetCheck) setSetColumns([{ name: '', lead_field: '', match_type: 'exact' }]);
      onChanged();
    } catch (e) {
      setOpMsg({ ok: false, text: e.response?.data?.detail || 'Create failed' });
    }
    setOpSaving(false);
  };

  // Unassign a list from this entity
  const unassignList = async (listId) => {
    setUnassigning(listId);
    try {
      await axios.delete(`${API}/targeting-lists/${listId}/unassign`, {
        params: { level, entity_id: entityId },
      });
      onChanged();
    } catch (e) {
      setOpMsg({ ok: false, text: e.response?.data?.detail || 'Unassign failed' });
    }
    setUnassigning(null);
  };

  // Open/close/save a per-list condition editor (Set Check only)
  const openConditionEditor = (tl) => {
    setEditingConditionId(tl.id);
    setCondField(tl.condition?.field || '');
    setCondValues(tl.condition?.in || []);
    setCondElseEnabled(!!(tl.condition?.else?.require_blank?.length));
    setCondElseFields(tl.condition?.else?.require_blank || []);
    setCondMsg(null);
  };
  const closeConditionEditor = () => {
    setEditingConditionId(null);
    setCondMsg(null);
  };
  const clearConditionForm = () => {
    setCondField(''); setCondValues([]); setCondElseEnabled(false); setCondElseFields([]);
  };
  const saveCondition = async (listId) => {
    setCondSaving(true);
    setCondMsg(null);
    let condition = null;
    if (condField && condValues.length > 0) {
      condition = { field: condField, in: condValues };
      if (condElseEnabled && condElseFields.length > 0) {
        condition.else = { require_blank: condElseFields };
      }
    }
    try {
      await axios.put(`${API}/targeting-lists/${listId}/assignments/condition`, { condition }, {
        params: { level, entity_id: entityId },
      });
      setEditingConditionId(null);
      onChanged();
    } catch (e) {
      setCondMsg({ ok: false, text: e.response?.data?.detail || 'Save failed' });
    }
    setCondSaving(false);
  };

  // Already-assigned list IDs for filtering the connect dropdown
  const assignedIds = new Set((assignedLists || []).map(l => l.id));

  return (
    <div style={{ marginTop: '10px', paddingLeft: '26px', position: 'relative', zIndex: connectOpen ? 10 : 'auto' }}>
      {/* Assigned lists */}
      {assignedLists && assignedLists.length > 0 ? (
        <div style={{ marginBottom: '8px' }}>
          {assignedLists.map(tl => {
            const { here, elsewhere } = assignmentPlaces(tl, level, entityId);
            const elsewhereText = elsewhere.map(placeLabel).join(', ');
            return (
            <div key={tl.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 4px 2px 8px', borderRadius: '12px', background: '#eef4ff', border: '1px solid #c0d4f5', marginRight: '6px', marginBottom: '4px', fontSize: '11px', color: '#2c5aa0' }}>
              <span title={sourceTooltip(tl.source)}>
                {tl.list_name}{tl.match_field ? ` (${tl.match_field})` : ''} — {tl.item_count} items{tl.condition ? ` · gated by ${tl.condition.field}` : ''}{sourceBadge(tl.source)}
                {!here && <span style={{ color: '#7f8c8d' }}> · set at {elsewhereText}</span>}
              </span>
              <button
                onClick={() => window.open(`/targeting-lists?selectListId=${tl.id}`, '_blank', 'noopener,noreferrer')}
                title="View / edit this list in List Management (opens in a new tab)"
                style={{ border: 'none', background: 'transparent', color: '#2c5aa0', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: '0 2px' }}
              >↗</button>
              {isSetCheck && canManage && here && (
                <button
                  onClick={() => editingConditionId === tl.id ? closeConditionEditor() : openConditionEditor(tl)}
                  title="Condition — applies to just this list"
                  style={{ border: 'none', background: 'transparent', color: tl.condition ? '#1a7f4b' : '#2c5aa0', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: '0 2px' }}
                >⚙{tl.condition ? '✓' : ''}</button>
              )}
              {tl.source?.kind === 'carryover_domain_counts' && canManage && (
                <button
                  onClick={() => refreshGenerated(tl)}
                  disabled={refreshing === tl.id}
                  title="Recount from the source program(s)"
                  style={{ border: 'none', background: 'transparent', color: '#2c5aa0', cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: '0 2px', opacity: refreshing === tl.id ? 0.5 : 1 }}
                >↻</button>
              )}
              {canManage && here && (
                <button
                  onClick={() => unassignList(tl.id)}
                  disabled={unassigning === tl.id}
                  title={elsewhere.length
                    ? `Unassign from this ${level} only. It is also assigned at ${elsewhereText}, which stays until removed there.`
                    : 'Unassign this list'}
                  style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px', opacity: unassigning === tl.id ? 0.5 : 1 }}
                >×</button>
              )}
              {canManage && !here && (
                <span
                  title={`Assigned at ${elsewhereText}, not at this ${level}, so it can't be removed from here. Open that ${elsewhere[0]?.level || 'level'}'s own QA Rules page to remove it.`}
                  style={{ color: '#7f8c8d', cursor: 'help', fontSize: '12px', lineHeight: 1, padding: '0 4px' }}
                >ⓘ</span>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        <div style={{ fontSize: '11px', color: '#e67e22', marginBottom: '8px' }}>
          ⚠ No lists assigned — check will pass all leads until at least one list is added.
        </div>
      )}

      {/* Per-list condition editor (Set Check only) — "only apply THIS list
          when [field] is one of [...]", independent of the rule-level
          condition CheckRow renders for the whole check. Explicit Save/
          Cancel — see state declarations above for why. */}
      {isSetCheck && editingConditionId != null && (assignedLists || []).some(tl => tl.id === editingConditionId) && (
        <div style={{ marginBottom: '10px', padding: '10px 12px', background: '#f7f9fc', border: '1px solid #dde4ee', borderRadius: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#888', letterSpacing: '0.03em', marginBottom: '8px' }}>
            CONDITION FOR "{(assignedLists.find(tl => tl.id === editingConditionId) || {}).list_name}" — APPLIES TO JUST THE SELECTED LIST
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', color: '#2c3e50' }}>
            <span>Only apply this list when</span>
            <SearchableSelect
              options={buildMatchFieldOptions(allLeadFields, condField)}
              value={condField}
              onChange={v => setCondField(v)}
              placeholder="a field…"
            />
            <span>is one of</span>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <TagInput
                tags={condValues}
                onChange={v => setCondValues(v)}
                placeholder="Type a value + Enter…"
              />
            </div>
            {(condField || condValues.length > 0) && (
              <button type="button" onClick={clearConditionForm} style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}>
                Clear
              </button>
            )}
          </div>
          {condField && condValues.length > 0 && (
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', fontSize: '13px', color: '#2c3e50' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input type="checkbox" checked={condElseEnabled} onChange={e => setCondElseEnabled(e.target.checked)} />
                Otherwise, require
              </label>
              {condElseEnabled && (
                <div style={{ minWidth: '200px' }}>
                  <MultiSelect
                    options={(allLeadFields || []).map(f => ({ value: f.key, label: f.label }))}
                    values={condElseFields}
                    onChange={v => setCondElseFields(v)}
                    placeholder="a field…"
                  />
                </div>
              )}
              {condElseEnabled && <span>to be blank</span>}
            </div>
          )}
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => saveCondition(editingConditionId)}
              disabled={condSaving}
              style={{ padding: '5px 14px', border: 'none', borderRadius: '4px', background: '#2980b9', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '12px', opacity: condSaving ? 0.6 : 1 }}
            >
              {condSaving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={closeConditionEditor} style={{ padding: '5px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', color: '#666', cursor: 'pointer', fontSize: '12px' }}>
              Cancel
            </button>
            {condMsg && (
              <span style={{ fontSize: '11px', color: condMsg.ok ? '#1a7f4b' : '#c0392b' }}>{condMsg.text}</span>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {canManage && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={openConnect} style={{ fontSize: '11px', padding: '3px 10px', border: '1px solid #2980b9', borderRadius: '4px', background: connectOpen ? '#2980b9' : '#fff', color: connectOpen ? '#fff' : '#2980b9', cursor: 'pointer', fontWeight: '600' }}>
            ＋ Connect existing list
          </button>
          <button onClick={openCreate} style={{ fontSize: '11px', padding: '3px 10px', border: '1px solid #1a7f4b', borderRadius: '4px', background: createOpen ? '#1a7f4b' : '#fff', color: createOpen ? '#fff' : '#1a7f4b', cursor: 'pointer', fontWeight: '600' }}>
            ＋ Create new list
          </button>
        </div>
      )}

      {/* Connect existing panel */}
      {connectOpen && (
        <div style={{ marginTop: '10px', background: '#f5f8ff', border: '1px solid #c0d4f5', borderRadius: '6px', padding: '12px 14px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#2c5aa0', marginBottom: '8px' }}>Connect existing {listType} list</div>
          {loadingAvail ? (
            <div style={{ fontSize: '12px', color: '#aaa' }}>Loading lists…</div>
          ) : (
            <>
              <SearchableSelect
                options={availableLists.filter(l => !assignedIds.has(l.id)).map(l => ({
                  value: l.id,
                  label: `${l.list_name}${l.match_field ? ` (${l.match_field})` : ''}`,
                }))}
                value={selectedListId}
                onChange={setSelectedListId}
                placeholder={`— search ${listType} lists —`}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button
                  onClick={() => selectedListId && assignList(selectedListId)}
                  disabled={!selectedListId || opSaving}
                  style={{ padding: '5px 14px', border: 'none', borderRadius: '4px', background: selectedListId ? '#2980b9' : '#ccc', color: '#fff', cursor: selectedListId ? 'pointer' : 'default', fontWeight: '600', fontSize: '12px' }}
                >
                  {opSaving ? 'Assigning…' : 'Assign'}
                </button>
                <button onClick={() => { setConnectOpen(false); setSelectedListId(''); }} style={{ padding: '5px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', color: '#666', cursor: 'pointer', fontSize: '12px' }}>
                  Cancel
                </button>
              </div>
              {availableLists.filter(l => !assignedIds.has(l.id)).length === 0 && !loadingAvail && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#888' }}>No unassigned {listType} lists found. Use "Create new list" to add one.</div>
              )}
            </>
          )}
        </div>
      )}

      {/* Create new list panel */}
      {createOpen && (
        <div style={{ marginTop: '10px', background: '#f5fef8', border: '1px solid #a9dfbf', borderRadius: '6px', padding: '12px 14px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#1a7f4b', marginBottom: '10px' }}>Create new {listType} list and assign</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* List name */}
            <div>
              <label style={{ ...labelStyle, marginBottom: '4px' }}>List name</label>
              <input
                value={newListName}
                onChange={e => setNewListName(e.target.value)}
                placeholder={`e.g. ${checkName === 'geo' ? 'Target States Q1 2025' : checkName === 'segment' ? 'Enterprise Segments' : checkName === 'tal' ? 'Q3 Target Accounts' : checkName === 'industry' ? 'Target Industries' : `${listType} List`}`}
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 8px', fontSize: '13px' }}
              />
            </div>
            {/* Geo: state vs country radio */}
            {checkName === 'geo' && (
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Field:</span>
                {[['state', 'State'], ['country', 'Country']].map(([val, lbl]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', cursor: 'pointer' }}>
                    <input type="radio" value={val} checked={geoField === val} onChange={() => setGeoField(val)} />
                    {lbl}
                  </label>
                ))}
              </div>
            )}
            {/* Set Check: PM-defined columns (name + matching Lead field + match strategy) */}
            {isSetCheck && (
              <div>
                <label style={{ ...labelStyle, marginBottom: '4px' }}>Columns</label>
                <SetColumnEditor columns={setColumns} onChange={setSetColumns} allLeadFields={allLeadFields} />
              </div>
            )}
            {/* Values textarea */}
            <div>
              <label style={{ ...labelStyle, marginBottom: '4px' }}>
                {effectiveColumns
                  ? `Rows — paste one per line, columns separated by tab or comma (${effectiveColumns.join(', ')})`
                  : `Values — paste one per line${checkName === 'geo' ? ` (${geoField} names or abbreviations)` : ''}`}
              </label>
              <textarea
                value={newListValues}
                onChange={e => setNewListValues(e.target.value)}
                placeholder={effectiveColumns
                  ? effectiveColumns.map((c, i) => (i === 0 ? 'example-1' : `${c}-1`)).join(', ') + '\n' + effectiveColumns.map((c, i) => (i === 0 ? 'example-2' : `${c}-2`)).join(', ')
                  : checkName === 'geo'
                  ? 'California\nTexas\nNew York\nFL\nCA'
                  : checkName === 'segment'
                  ? 'Enterprise\nMid-Market\nSMB'
                  : checkName === 'tal'
                  ? 'Acme Corp\nGlobal Industries\nTechCo Inc'
                  : 'Technology\nFinancial Services\nHealthcare'}
                rows={5}
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 8px', fontSize: '12px', resize: 'vertical', fontFamily: 'inherit' }}
              />
              {newListValues && (
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  {effectiveColumns ? parseMultiColumnPaste(newListValues, effectiveColumns).length : newListValues.split('\n').filter(v => v.trim()).length} {effectiveColumns ? 'rows' : 'values'}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={createAndAssign}
                disabled={!newListName.trim() || opSaving}
                style={{ padding: '6px 16px', border: 'none', borderRadius: '4px', background: newListName.trim() ? '#1a7f4b' : '#ccc', color: '#fff', cursor: newListName.trim() ? 'pointer' : 'default', fontWeight: '600', fontSize: '12px' }}
              >
                {opSaving ? 'Creating…' : `Create & Assign to ${level || 'entity'}`}
              </button>
              <button onClick={() => setCreateOpen(false)} style={{ padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', color: '#666', cursor: 'pointer', fontSize: '12px' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Op message */}
      {opMsg && (
        <div style={{ marginTop: '8px', fontSize: '12px', padding: '6px 10px', borderRadius: '4px', background: opMsg.ok ? '#eafaf1' : '#fdedec', color: opMsg.ok ? '#1e8449' : '#c0392b', border: `1px solid ${opMsg.ok ? '#a9dfbf' : '#f5b7b1'}` }}>
          {opMsg.text}
          <button onClick={() => setOpMsg(null)} style={{ marginLeft: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontWeight: '700' }}>×</button>
        </div>
      )}
    </div>
  );
}

// Per-keyword match logic. "whole_word" is the original — and until now, only —
// behavior, so it stays the default for both new keywords and any pre-existing
// keyword missing this field entirely (see qa_checks.py's _keyword_matches()).
const MATCH_TYPE_OPTIONS = [
  { value: 'whole_word', label: 'Whole word' },
  { value: 'exact',      label: 'Exact' },
  { value: 'contains',   label: 'Contains' },
  { value: 'fuzzy',      label: 'Fuzzy' },
];

// Convert legacy config (approved_keywords/reject_keywords) to new keywords array format
function legacyToKeywords(config_json) {
  if (!config_json) return [];
  if (config_json.keywords) return config_json.keywords;
  const approved = (config_json.approved_keywords || []).map(k => ({ keyword: k, category: 'secondary' }));
  const reject   = (config_json.reject_keywords   || []).map(k => ({ keyword: k, category: 'reject'    }));
  return [...approved, ...reject];
}

// Required fields — seed a {field_key: tier} map from a saved rule, with
// back-compat for rules saved before per-field tiers existed (config_json used
// to be {required_field_keys: [...]} with one severity for the whole check —
// every listed key maps to that single tier here, matching historical
// behavior). An intentionally empty map is respected as-is, never silently
// replaced; when there's no rule yet, falls back to the real hardcoded default
// so a PM opening this for the first time sees today's actual defaults
// pre-filled instead of a blank checklist.
function seedFieldSettings(rule, defaultFieldSettings) {
  if (rule?.config_json?.field_settings) return { ...rule.config_json.field_settings };
  if (rule?.config_json?.required_field_keys) {
    const tier = rule.severity === 'review' ? 'review' : 'reject';
    return Object.fromEntries(rule.config_json.required_field_keys.map(k => [k, tier]));
  }
  if (!rule) return { ...(defaultFieldSettings || {}) };
  return {};
}

const TIER_OPTIONS = [
  { value: '',         label: 'Not required' },
  { value: 'reject',   label: 'Required — Client' },
  { value: 'review',   label: 'Required — Supplier' },
  { value: 'optional', label: 'Optional' },
];

// Mirrors backend leads.py's DATE_FORMAT_PATTERNS/DEFAULT_DATE_FORMAT exactly
// (same vocabulary ExportConfigPanel.js's own DATE_FORMAT_OPTIONS uses) — but
// this one sets required_fields' config_json.date_format, the client's own
// date convention for AUTOMATED SENDS (Client Delivery Wizard + CSV
// delivery), not a manual export download's date_format.
const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (e.g. 07/15/2026)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (e.g. 15/07/2026)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (e.g. 2026-07-15)' },
  { value: 'M/D/YY', label: 'M/D/YY — short date (e.g. 7/15/26)' },
];

// Some connectors (Convertr) name a field_mapping's target key like
// "form[mobile_phone]" — a form-post-array convention, not the field name
// itself. Passed whole into bestFieldMatch(), the "form[" wrapper knocks the
// query out of exact-match range for every candidate, so two fields whose
// names are substrings of each other (e.g. "phone"/"mobile_phone",
// "industry"/"sub_Industry__c") tie at the same 0.85 partial-substring score
// and whichever field sorts first in allLeadFields silently wins — hiding
// the real one. Stripping the wrapper first restores an exact match (score
// 1) for the real target field, so it beats every partial-substring
// competitor outright. No-op for connectors with flat target keys (Marketo,
// Integrate). Real production case (2026-08-18): Egnyte's Convertr
// destinations use this wrapper, and mobile_phone/sub_Industry__c/
// asset_downloaded_del__c all displayed as mapped to the WRONG field here,
// which is also why they never got picked up by the client-template sync.
const TARGET_FIELD_WRAPPER_RE = /^\w+\[(.+)\]$/;
function stripTargetFieldWrapper(targetField) {
  const m = TARGET_FIELD_WRAPPER_RE.exec(targetField || '');
  return m ? m[1] : targetField;
}

// Aggressive case/space/punctuation-insensitive key, matching backend
// lead_delivery.py's own _norm() — a destination's field_mapping CSV-column
// value is free text (typed, fuzzy-matched, or pulled from a sample file), so
// comparing it against a field's key/label/aliases needs the same loose
// normalization the delivery engine itself uses for header matching.
// For one Lead field, find every active destination whose field_mapping entry
// resolves to this field — checked against the field's key, NeTRA label,
// standard upload aliases, and this rule's own client-specific alias, reusing
// bestFieldMatch() (fileFieldMatch.js) rather than a hand-rolled exact-only
// comparison. Two real production gaps this fixes over a plain string-equality
// check:
//   1. A blank field_mapping csv_column means "auto-match the header at
//      release time" (see DestinationForm's own hint text, and
//      lead_delivery.py's _resolve_mapping()) — the destination's real
//      behavior falls back to comparing the *target field's own name*
//      against the release file's headers, so this does the same instead of
//      treating blank as "unmapped" and skipping it outright.
//   2. A real destination's csv_column is often a qualified label — e.g.
//      "Email Address" or "Mobile Phone Number" rather than the exact
//      "Email"/"Mobile_Phone" — bestFieldMatch's substring/similarity scoring
//      catches those; a strict normalized-equality check (the original
//      implementation) missed them silently.
// Returns [{targetField, destinationName}], one entry per matching destination.
// Attributes every active destination's field_mapping entry to at most ONE
// Lead field — the single best match across the WHOLE field list, which is
// how bestFieldMatch is used everywhere else in this codebase. Matching each
// field independently against only its own candidates (an earlier version of
// this fix) let two fields whose names are substrings of each other — e.g.
// "phone" and "mobilePhone" — both claim the same mapping entry, since
// "mobilephone" contains "phone" as a substring no matter which field's
// candidates were being checked against it. Matching against every field at
// once lets the exact match (score 1) beat the substring match (score 0.85)
// and pick a single winner. Returns {fieldKey: [{targetField, destinationName}]}.
function buildApiFieldMap(destinations, allLeadFields, fieldAliases) {
  const candidateFields = [];
  (allLeadFields || []).forEach(f => {
    candidateFields.push({ key: f.key, label: f.label, _fieldKey: f.key });
    (f.aliases || []).forEach(a => candidateFields.push({ key: a, label: a, _fieldKey: f.key }));
    const alias = fieldAliases[f.key];
    if (alias) candidateFields.push({ key: alias, label: alias, _fieldKey: f.key });
  });
  const byFieldKey = {};
  (destinations || []).forEach(d => {
    Object.entries(d.field_mapping || {}).forEach(([targetField, csvColumn]) => {
      const trimmedCsvColumn = (csvColumn || '').trim();
      const headerText = trimmedCsvColumn || stripTargetFieldWrapper(targetField);
      if (!headerText) return;
      const match = bestFieldMatch(headerText, candidateFields);
      if (match) {
        const fieldKey = match.field._fieldKey;
        const destinationName = d.name || d.connection_name || `Destination ${d.id}`;
        // csvColumn kept raw (not the matched headerText) so the drift check
        // below can tell "blank = auto-match, not alias-driven" apart from
        // "explicit, and it happens to equal the current alias."
        (byFieldKey[fieldKey] = byFieldKey[fieldKey] || []).push({ targetField, destinationName, csvColumn: trimmedCsvColumn });
      }
    });
  });
  return byFieldKey;
}

// Read-only reconciliation: does what a field is ACTUALLY wired to (a live
// Destination's csv_column, or the auto-generated "{program} - client
// template" Export Config) still match its Client Alias? Both of the drifts
// this catches are exactly the bug class that took a from-scratch manual
// audit to find on both staging and production the same day this was
// written (see the "Client Field-Mapping Setup" design memo) — surfacing it
// here means the next client's setup self-diagnoses instead of requiring
// that same manual comparison again. Pure read/display — never writes
// anything, never changes what release-csv/push_lead actually consume.
const _normalizeHeader = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function buildFieldDrift(apiFieldMap, fieldSettings, fieldAliases, exportConfigs, programName) {
  const drift = {};
  const push = (key, msg) => { (drift[key] = drift[key] || []).push(msg); };

  // 1) Destination vs. Client Alias — an explicit (non-blank) csv_column
  //    that doesn't match the alias will make release-csv's preflight fail
  //    to find that column in an export built from the alias.
  Object.entries(apiFieldMap || {}).forEach(([fieldKey, hits]) => {
    const alias = fieldAliases[fieldKey];
    if (!alias) return; // nothing to compare against
    hits.forEach(hit => {
      if (!hit.csvColumn) return; // blank = auto-match on the target field's own name, not alias-driven
      if (_normalizeHeader(hit.csvColumn) !== _normalizeHeader(alias)) {
        push(fieldKey, `"${hit.destinationName}" expects the column "${hit.csvColumn}", but Client Alias is "${alias}".`);
      }
    });
  });

  // 2) Auto-generated "client template" Export Config vs. Client Alias —
  //    meaningful for Required — Client (reject-tier) fields, PLUS any field
  //    a live destination actually references (same expanded rule as
  //    syncExportTemplate()'s clientKeys and the backend's
  //    _sync_export_templates_from_required_fields — see either's comment
  //    for why a reject-tier-only rule was wrong: Armis's real destination
  //    needs mobile_phone/naics_code, both tier "optional").
  //
  //    Iterates the UNION of fieldSettings keys and apiFieldMap keys, not
  //    just fieldSettings — a field with NO tier at all ("Not required",
  //    which deletes it from fieldSettings entirely, see setFieldTier())
  //    used to be invisible to this whole check even when a live
  //    destination genuinely referenced it, since the loop only ever walked
  //    Object.entries(fieldSettings). Real production case (2026-08-18):
  //    Egnyte's Convertr destinations reference "address", but Address had
  //    no tier set at all — the API mapping pill showed correctly, yet
  //    nothing ever flagged that it was still missing from the client
  //    template, because the untiered field was never visited.
  const clientTemplate = (exportConfigs || []).find(c => c.name === `${programName} - client template`);
  const allFieldKeys = new Set([...Object.keys(fieldSettings || {}), ...Object.keys(apiFieldMap || {})]);
  allFieldKeys.forEach((fieldKey) => {
    const tier = (fieldSettings || {})[fieldKey];
    const isClientTemplateField = tier === 'reject' || (apiFieldMap[fieldKey] || []).length > 0;
    if (!isClientTemplateField) return;
    const alias = fieldAliases[fieldKey];
    if (tier === undefined && (apiFieldMap[fieldKey] || []).length > 0) {
      push(fieldKey, `Referenced by a live API destination (${apiFieldMap[fieldKey].map(h => h.destinationName).join(', ')}) but has no Required Fields tier set — won't be tracked or exported.`);
      return; // no tier means no client-template membership to check below either
    }
    if (!alias) return;
    if (!clientTemplate) return; // hasn't been created yet — nothing to compare, not itself a drift
    if (!(clientTemplate.selected_fields || []).includes(fieldKey)) {
      push(fieldKey, `Missing from the "${programName} - client template" export — won't appear in Accepted exports.`);
      return;
    }
    const exportLabel = (clientTemplate.field_mapping || {})[fieldKey];
    if (exportLabel && _normalizeHeader(exportLabel) !== _normalizeHeader(alias)) {
      push(fieldKey, `Export label is "${exportLabel}", but Client Alias is "${alias}".`);
    }
  });

  return drift;
}

// Upsert (by name, since LeadExportConfig has no natural business key beyond
// that) a saved export config reflecting one of the two Required Fields
// templates — "{program} - supplier template" (every tracked field) /
// "{program} - client template" (Required — Client fields only). Best-effort:
// a failure here shouldn't surface as an error against the required_fields
// rule's own save, which already succeeded by the time this runs. A field's
// client alias (if set) becomes that field's custom export label — the
// export config's own built-in mechanism for "what this program calls it,"
// no compound-string trick needed here the way the raw CSV template needs.
async function syncExportTemplate(programId, name, fieldKeys, fieldAliases) {
  if (!programId || !name || fieldKeys.length === 0) return;
  try {
    const existing = await axios.get(`${API}/export-configs/`, { params: { program_id: programId } }).then(r => r.data || []);
    const match = existing.find(c => c.name === name);
    const field_mapping = {};
    fieldKeys.forEach(k => { if (fieldAliases[k]) field_mapping[k] = fieldAliases[k]; });
    if (match) {
      await axios.put(`${API}/export-configs/${match.id}`, { selected_fields: fieldKeys, field_mapping });
    } else {
      await axios.post(`${API}/export-configs/`, { program_id: programId, name, selected_fields: fieldKeys, field_mapping });
    }
  } catch {
    // Silent — this is a background convenience sync, not a user action.
  }
}

// "Generate from prior program" for the Carryover List check (2026-10-06).
// Counts a prior program's leads per domain server-side (same domain rule QA
// itself uses — backend app/utils/carryover.py), saves them as a snapshot
// Carryover Domain Counts list, attaches it at this level, and sets the
// carryover rule's max per domain in one call. The cap isn't optional: without
// it check_carryover() ignores domain counts entirely — which is how a real
// program's carryover rule once sat active and did nothing. Page-local, not
// src/components/, since it fetches its own data.
const CARRYOVER_STATUS_OPTIONS = [
  { value: 'Client Accepted', label: 'Client Accepted', hint: 'what the client actually counted' },
  { value: 'DemandArc Accepted', label: 'DemandArc Accepted', hint: 'for a prior program still mid-delivery' },
  { value: 'Approved Pending Call Review', label: 'Approved Pending Call Review', hint: '' },
];

function CarryoverGenerateModal({ programs, targetProgramId, level, entityId, defaultMax, onClose, onGenerated }) {
  const targetProgram = (programs || []).find(p => String(p.id) === String(targetProgramId));
  const [allClients, setAllClients] = useState(false);
  const [sourceIds, setSourceIds] = useState([]);
  const [statuses, setStatuses] = useState(['Client Accepted']);
  const [maxPerDomain, setMaxPerDomain] = useState(defaultMax ? String(defaultMax) : '6');
  const [listName, setListName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null); // 'preview' | 'generate'
  const [error, setError] = useState(null);

  const programOptions = (programs || [])
    .filter(p => String(p.id) !== String(targetProgramId))
    .filter(p => allClients || !targetProgram || p.client_id === targetProgram.client_id)
    .map(p => ({ value: String(p.id), label: p.program_name || `Program ${p.id}` }));

  // Any change to what's being counted makes an earlier preview stale.
  const changeSource = (ids) => { setSourceIds(ids); setPreview(null); setError(null); };
  const toggleStatus = (st) => {
    setStatuses(prev => prev.includes(st) ? prev.filter(x => x !== st) : [...prev, st]);
    setPreview(null); setError(null);
  };

  const body = { source_program_ids: sourceIds.map(Number), statuses };
  const runPreview = () => {
    setBusy('preview'); setError(null);
    axios.post(`${API}/targeting-lists/carryover-domain-counts/preview`, body)
      .then(r => setPreview(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Preview failed'))
      .finally(() => setBusy(null));
  };
  const runGenerate = () => {
    setBusy('generate'); setError(null);
    axios.post(`${API}/targeting-lists/carryover-domain-counts`, {
      ...body, level, entity_id: Number(entityId), max_per_domain: Number(maxPerDomain),
      list_name: listName.trim() || null,
    })
      .then(r => onGenerated(r.data))
      .catch(e => { setError(e.response?.data?.detail || 'Generate failed'); setBusy(null); });
  };

  const maxOk = Number(maxPerDomain) >= 1 && Number.isInteger(Number(maxPerDomain));
  const canPreview = sourceIds.length > 0 && statuses.length > 0 && !busy;
  const canGenerate = !!preview && preview.domain_count > 0 && maxOk && !busy;
  const label = { fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={() => !busy && onClose()}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '8px', width: 'min(620px, 96vw)', maxHeight: '90vh', overflowY: 'auto', padding: '18px 20px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>Generate carryover domain counts</div>
        <div style={{ fontSize: '12px', color: '#777', marginBottom: '14px' }}>
          Counts the prior program’s leads per domain, saves them as a Carryover Domain Counts list, attaches it here, and sets the max per domain. Each domain’s prior count is subtracted from that max for this phase.
        </div>

        <div style={{ marginBottom: '12px' }}>
          <MultiSelect label="Prior program(s)" options={programOptions} values={sourceIds} onChange={changeSource}
            placeholder="Pick the program(s) to carry over from…" emptyText="No other programs for this client" />
          <label style={{ fontSize: '11px', color: '#888', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
            <input type="checkbox" checked={allClients} onChange={e => setAllClients(e.target.checked)} />
            Show programs from every client
          </label>
          {sourceIds.length > 1 && (
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>Counts are summed across the selected programs (a cumulative list).</div>
          )}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <div style={label}>Count leads with status</div>
          {CARRYOVER_STATUS_OPTIONS.map(o => (
            <label key={o.value} style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '2px' }}>
              <input type="checkbox" checked={statuses.includes(o.value)} onChange={() => toggleStatus(o.value)} style={{ marginRight: '6px' }} />
              {o.label}{o.hint && <span style={{ color: '#999' }}> — {o.hint}</span>}
            </label>
          ))}
          <div style={{ fontSize: '11px', color: '#999' }}>Rejected leads never count.</div>
        </div>

        <button onClick={runPreview} disabled={!canPreview}
          style={{ padding: '5px 12px', fontSize: '12px', borderRadius: '4px', border: '1px solid #3498db', background: '#fff', color: '#3498db', cursor: canPreview ? 'pointer' : 'default', opacity: canPreview ? 1 : 0.5 }}>
          {busy === 'preview' ? 'Counting…' : 'Preview counts'}
        </button>

        {preview && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f7f9fc', border: '1px solid #dde4ee', borderRadius: '6px', fontSize: '12px', color: '#444' }}>
            <div style={{ fontWeight: '600', marginBottom: '6px' }}>
              {preview.lead_count.toLocaleString()} leads → {preview.domain_count.toLocaleString()} domains
            </div>
            {preview.domain_count > 0 && (
              <>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  {Object.entries(preview.histogram).map(([k, n]) => (
                    <span key={k}><span style={{ color: '#888' }}>{k} lead{k === '1' ? '' : 's'}:</span> {n}</span>
                  ))}
                </div>
                <div style={{ color: '#666' }}>
                  Top: {preview.top.slice(0, 5).map(t => `${t.domain} (${t.count})`).join(', ')}
                </div>
              </>
            )}
            {preview.no_domain_count > 0 && (
              <div style={{ color: '#e67e22', marginTop: '4px' }}>⚠ {preview.no_domain_count} lead(s) have no company domain or email domain and can’t be carried over.</div>
            )}
            {preview.domain_count === 0 && (
              <div style={{ color: '#e67e22' }}>No leads in those statuses — nothing to carry over.</div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '14px' }}>
          <div>
            <div style={label}>Max per domain (prior + new)</div>
            <input type="number" min="1" value={maxPerDomain} onChange={e => setMaxPerDomain(e.target.value)}
              style={{ width: '80px', padding: '4px 8px', border: `1px solid ${maxOk ? '#ccc' : '#e74c3c'}`, borderRadius: '4px', fontSize: '13px' }} />
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={label}>List name <span style={{ fontWeight: 400, color: '#999' }}>(optional)</span></div>
            <input value={listName} onChange={e => setListName(e.target.value)} placeholder="Auto: “{program} — Domain Carryover — {date}”"
              style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }} />
          </div>
        </div>

        {error && <div style={{ marginTop: '10px', fontSize: '12px', color: '#c0392b' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <button onClick={onClose} disabled={!!busy}
            style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancel</button>
          <button onClick={runGenerate} disabled={!canGenerate}
            title={!preview ? 'Preview the counts first' : ''}
            style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '4px', border: 'none', background: '#27ae60', color: '#fff', cursor: canGenerate ? 'pointer' : 'default', opacity: canGenerate ? 1 : 0.5 }}>
            {busy === 'generate' ? 'Generating…' : 'Generate & attach'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CheckRow({ checkName, rule, inherited, targetingLists, sizeBands, allLeadFields, defaultFieldSettings, defaultDateFormat, apiDestinations, apiExportConfigs, programName, resolvedProgramId, onSave, saving, level, entityId, onListChange, overridesBelow, programs, domainCapMax }) {
  // Carryover List only — "Generate from prior program" modal + its result line.
  const [carryoverGenOpen, setCarryoverGenOpen] = useState(false);
  // Which of this card's list panels have a picker open (keyed by list type —
  // Carryover has two panels). See the container style below.
  const [openOverlays, setOpenOverlays] = useState({});
  const handleOverlayChange = useCallback((key, open) => {
    setOpenOverlays(prev => (!!prev[key] === open ? prev : { ...prev, [key]: open }));
  }, []);
  const [carryoverGenMsg, setCarryoverGenMsg] = useState(null);
  const meta = CHECK_META[checkName];
  const [active, setActive] = useState(rule ? rule.active : false);
  const [severity, setSeverity] = useState(rule ? rule.severity : 'reject');
  const [selectedBands, setSelectedBands] = useState(rule?.config_json?.allowed_bands || []);
  const [selectedFieldSettings, setSelectedFieldSettings] = useState(
    () => seedFieldSettings(rule, defaultFieldSettings)
  );
  // Client-specific header name per field (required_fields only) — e.g. a
  // client calls "Email Address" something else in their own file. Uploads
  // scoped to this program/component accept it as an alias alongside the
  // built-in FIELD_MAP_ALIASES. Kept as its own config_json key (field_aliases),
  // separate from selectedFieldSettings above.
  const [selectedFieldAliases, setSelectedFieldAliases] = useState(
    () => ({ ...(rule?.config_json?.field_aliases || {}) })
  );
  // This client's own date convention for AUTOMATED SENDS (Client Delivery
  // Wizard + the CSV delivery flow) — required_fields only, its own
  // config_json key (date_format), separate from LeadExportConfig.date_format
  // (which only affects a PM's manual CSV export/download, not what an
  // automated send transmits to the client's system). '' means "use the
  // system default" (DEFAULT_DATE_FORMAT, shown via defaultDateFormat below).
  const [selectedDateFormat, setSelectedDateFormat] = useState(rule?.config_json?.date_format || '');
  // Blank "-" / "N/A" style placeholders in uploaded values (default on; see utils/blankPlaceholders.js).
  const [blankPlaceholders, setBlankPlaceholders] = useState(() => placeholdersEnabled(rule?.config_json));
  // Per-field TEXT date conversion applied when a supplier's upload becomes
  // leads (config_json.field_date_formats, {field_key: {from, to}}) — e.g. a
  // custom Start Date that arrives DD-MM-YYYY and the client wants M/D/YY.
  // handleSave rebuilds config_json from scratch, so this MUST be carried
  // through it or the next save would silently drop the setting.
  const [selectedFieldDateFormats, setSelectedFieldDateFormats] = useState(
    () => ({ ...(rule?.config_json?.field_date_formats || {}) })
  );
  // Auto-populate client aliases from a dropped client mapping template
  // (required_fields only) — drag/drop a CSV/XLSX, fuzzy-match its header
  // row to Lead fields (same primitives ExportConfigPanel.js's "Auto-configure
  // from a file" uses), then the file's own header text becomes that field's
  // client alias on Apply — it's literally what the client calls the column.
  const [aliasUploadDragOver, setAliasUploadDragOver] = useState(false);
  const [aliasUploadFileName, setAliasUploadFileName] = useState(null);
  const [aliasUploadError, setAliasUploadError] = useState(null);
  const [aliasUploadHeaders, setAliasUploadHeaders] = useState(null); // string[] | null
  const [aliasMapping, setAliasMapping] = useState({}); // header -> field key ('' = skip)
  const aliasFileInputRef = useRef(null);
  // Title keywords: new format
  const [keywords, setKeywords] = useState(() => legacyToKeywords(rule?.config_json));
  // Title Keywords only — learn accepted / title-rejected titles into auto
  // lists (backend app/auto_titles.py). Saved on the rule's config_json.
  const [autoAcceptTitles, setAutoAcceptTitles] = useState(rule?.config_json?.auto_accept_titles === true);
  const [autoRejectTitles, setAutoRejectTitles] = useState(rule?.config_json?.auto_reject_titles === true);
  // Address Format only — skip leads outside the US/Canada. On unless the
  // rule explicitly says false (backend treats an absent key as on).
  const [bypassNonUsCa, setBypassNonUsCa] = useState(rule?.config_json?.bypass_non_us_ca !== false);
  const [seedingTitles, setSeedingTitles] = useState(false);
  const [seedTitlesMsg, setSeedTitlesMsg] = useState(null);
  const [newKwText, setNewKwText] = useState('');
  const [newKwCat, setNewKwCat] = useState('secondary');
  const [newKwMatchType, setNewKwMatchType] = useState('whole_word');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  // Employee size — custom band input
  const [customBandInput, setCustomBandInput] = useState('');
  // Asset alignment
  const [allowedAssets, setAllowedAssets] = useState(rule?.config_json?.allowed_assets || []);
  // Domain cap
  const [maxPerDomain, setMaxPerDomain] = useState(rule?.config_json?.max_per_domain ?? 5);
  // Carryover list
  const [carryoverEmails, setCarryoverEmails] = useState(() => (rule?.config_json?.carryover_emails || []).join('\n'));
  const [carryoverDomainText, setCarryoverDomainText] = useState(() => {
    const d = rule?.config_json?.carryover_domains || {};
    return Object.entries(d).map(([k, v]) => `${k}\t${v}`).join('\n');
  });
  const [carryoverMaxPerDomain, setCarryoverMaxPerDomain] = useState(rule?.config_json?.max_per_domain ?? '');
  // Set Check's "only apply when [field] is one of [...]" gate — see
  // backend qa_checks.py's _evaluate_condition()/_CONDITION_PROCEED and
  // qa_rules.py's PUT validation. conditionField '' means no condition set
  // (the check always applies, today's default behavior).
  const [conditionField, setConditionField] = useState(rule?.config_json?.condition?.field || '');
  const [conditionValues, setConditionValues] = useState(rule?.config_json?.condition?.in || []);
  const [elseRequireBlankEnabled, setElseRequireBlankEnabled] = useState(
    !!(rule?.config_json?.condition?.else?.require_blank?.length)
  );
  const [elseRequireBlankFields, setElseRequireBlankFields] = useState(
    rule?.config_json?.condition?.else?.require_blank || []
  );
  // Collapsed-by-default UI state for the condition editor below — seeded
  // once from whether a condition already exists at mount (so an existing
  // one is visible right away), never re-synced on later `rule` changes
  // (e.g. the echo-back from this row's own save) so the panel doesn't
  // collapse out from under the user mid-edit. Purely a display toggle, not
  // persisted — unrelated to dirty/save state.
  const [conditionExpanded, setConditionExpanded] = useState(
    () => !!(rule?.config_json?.condition?.field)
  );
  const [dirty, setDirty] = useState(false);
  // Auto-save: any edit (markDirty) schedules a debounced save of this row's
  // own settings, so a change can never sit around unsaved long enough for an
  // unrelated action elsewhere on the page (e.g. assigning a list on another
  // check, which triggers a full QA-context reload) to silently wipe it out.
  const [justSaved, setJustSaved] = useState(false);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    setActive(rule ? rule.active : false);
    setSeverity(rule ? rule.severity : 'reject');
    setSelectedBands(rule?.config_json?.allowed_bands || []);
    setSelectedFieldSettings(seedFieldSettings(rule, defaultFieldSettings));
    setSelectedFieldAliases({ ...(rule?.config_json?.field_aliases || {}) });
    setSelectedDateFormat(rule?.config_json?.date_format || '');
    setBlankPlaceholders(placeholdersEnabled(rule?.config_json));
    setSelectedFieldDateFormats({ ...(rule?.config_json?.field_date_formats || {}) });
    setKeywords(legacyToKeywords(rule?.config_json));
    setAutoAcceptTitles(rule?.config_json?.auto_accept_titles === true);
    setAutoRejectTitles(rule?.config_json?.auto_reject_titles === true);
    setBypassNonUsCa(rule?.config_json?.bypass_non_us_ca !== false);
    setAllowedAssets(rule?.config_json?.allowed_assets || []);
    setMaxPerDomain(rule?.config_json?.max_per_domain ?? 5);
    setCarryoverEmails((rule?.config_json?.carryover_emails || []).join('\n'));
    const d2 = rule?.config_json?.carryover_domains || {};
    setCarryoverDomainText(Object.entries(d2).map(([k, v]) => `${k}\t${v}`).join('\n'));
    setCarryoverMaxPerDomain(rule?.config_json?.max_per_domain ?? '');
    setConditionField(rule?.config_json?.condition?.field || '');
    setConditionValues(rule?.config_json?.condition?.in || []);
    setElseRequireBlankEnabled(!!(rule?.config_json?.condition?.else?.require_blank?.length));
    setElseRequireBlankFields(rule?.config_json?.condition?.else?.require_blank || []);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule]);

  const markDirty = () => setDirty(true);
  // Set Check's condition gate: the debounced auto-save collapses an
  // incomplete condition (a gating field picked but no trigger values yet,
  // or vice versa) to config_json: null, since there's nothing valid to
  // persist yet (handleSave()'s own set_check branch, below). If that
  // incomplete save round-trips before the user finishes, the server's null
  // echoes back and CheckRow's reset-from-rule effect faithfully applies it —
  // wiping the in-progress field/value selection back to blank mid-edit, a
  // real reported bug ("selecting the dropdown just flashed and reset").
  // Only mark the row dirty (arming the debounced save) once the resulting
  // condition is either complete (both field and values set) or fully
  // cleared (both empty) — never on the half-built state in between, so no
  // save fires until there's something valid — or nothing at all — to save.
  const markConditionDirtyIfComplete = (field, values) => {
    const complete = !!field && values.length > 0;
    const cleared = !field && values.length === 0;
    if (complete || cleared) markDirty();
  };
  // Same race, one level deeper: handleSave()'s set_check branch only adds
  // `else: {require_blank: [...]}` when the checkbox is on AND at least one
  // field is picked — checking the box alone (fields still empty) saves a
  // condition with no `else` key at all, and the echo-back then resets the
  // checkbox itself back to unchecked. The only genuinely incomplete state
  // is "enabled, but no fields yet" — every other combination (including
  // unchecking, regardless of whatever fields are still sitting in local
  // state) is exactly what handleSave() would produce anyway, so it's safe
  // to save immediately.
  const markElseDirtyIfComplete = (enabled, fields) => {
    if (enabled && fields.length === 0) return;
    markDirty();
  };
  const handleToggleBand = (band) => {
    setSelectedBands(prev => prev.includes(band) ? prev.filter(b => b !== band) : [...prev, band]);
    markDirty();
  };
  const setFieldTier = (key, tier) => {
    setSelectedFieldSettings(prev => {
      const next = { ...prev };
      if (!tier) delete next[key]; else next[key] = tier;
      return next;
    });
    markDirty();
  };
  const setFieldAlias = (key, alias) => {
    setSelectedFieldAliases(prev => {
      const next = { ...prev };
      if (!alias) delete next[key]; else next[key] = alias;
      return next;
    });
    markDirty();
  };
  const setFieldDateFormat = (key, value) => {
    setSelectedFieldDateFormats(prev => {
      const next = { ...prev };
      const spec = valueToConversion(value);
      if (!spec) delete next[key]; else next[key] = spec;
      return next;
    });
    markDirty();
  };
  const setGroupTier = (groupFields, tier) => {
    setSelectedFieldSettings(prev => {
      const next = { ...prev };
      groupFields.forEach(f => { if (!tier) delete next[f.key]; else next[f.key] = tier; });
      return next;
    });
    markDirty();
  };

  // --- Auto-populate client aliases from a dropped file -------------------
  const handleAliasFileSelected = async (file) => {
    if (!file) return;
    setAliasUploadError(null);
    setAliasUploadFileName(file.name);
    setAliasUploadHeaders(null);
    setAliasMapping({});
    try {
      const headers = await readFileHeaderRow(file);
      if (!headers.length) {
        setAliasUploadError('No header row found in that file.');
        return;
      }
      const initialMapping = {};
      headers.forEach(h => {
        const m = bestFieldMatch(h, allLeadFields || []);
        initialMapping[h] = m ? m.field.key : '';
      });
      setAliasUploadHeaders(headers);
      setAliasMapping(initialMapping);
    } catch (e) {
      setAliasUploadError(e.message || 'Could not read that file — is it a valid CSV or XLSX?');
    }
  };
  const handleAliasFileInputChange = (e) => {
    const file = e.target.files?.[0];
    handleAliasFileSelected(file);
    e.target.value = '';
  };
  const handleAliasDrop = (e) => {
    e.preventDefault();
    setAliasUploadDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleAliasFileSelected(file);
  };
  const discardAliasUpload = () => {
    setAliasUploadFileName(null);
    setAliasUploadHeaders(null);
    setAliasMapping({});
    setAliasUploadError(null);
  };
  // Applies each confirmed header -> field mapping as that field's client
  // alias (the file's own header text, verbatim — it's literally what the
  // client calls the column) via the existing per-field setFieldAlias(), so
  // this behaves exactly like typing each alias in by hand from here on —
  // nothing is saved to the backend until the normal Save further down.
  const applyAliasMapping = () => {
    aliasUploadHeaders.forEach(h => {
      const key = aliasMapping[h];
      if (key) setFieldAlias(key, h);
    });
    discardAliasUpload();
  };
  const aliasMappedCount = new Set(Object.values(aliasMapping).filter(Boolean)).size;
  const aliasHasDuplicateMapping = aliasUploadHeaders && new Set(Object.values(aliasMapping).filter(Boolean)).size
    < Object.values(aliasMapping).filter(Boolean).length;
  const handleSave = () => {
    let config_json = null;
    if (checkName === 'employee_size') config_json = { allowed_bands: selectedBands };
    if (checkName === 'required_fields') config_json = withPlaceholderSetting({
      field_settings: selectedFieldSettings, field_aliases: selectedFieldAliases, date_format: selectedDateFormat || undefined,
      ...(Object.keys(selectedFieldDateFormats).length ? { field_date_formats: selectedFieldDateFormats } : {}),
    }, blankPlaceholders);
    if (checkName === 'title_keywords') config_json = {
      keywords,
      ...(autoAcceptTitles ? { auto_accept_titles: true } : {}),
      ...(autoRejectTitles ? { auto_reject_titles: true } : {}),
    };
    if (checkName === 'asset_alignment') config_json = { allowed_assets: allowedAssets };
    if (checkName === 'address') config_json = { bypass_non_us_ca: bypassNonUsCa };
    if (checkName === 'domain_cap') config_json = { max_per_domain: Number(maxPerDomain) || 5 };
    if (checkName === 'carryover_list') {
      const emails = carryoverEmails.split('\n').map(e => e.trim().toLowerCase()).filter(Boolean);
      const domains = {};
      carryoverDomainText.split('\n').forEach(line => {
        const parts = line.trim().split(/[\t,]+/);
        if (parts.length >= 2) {
          const dom = parts[0].trim().toLowerCase();
          const cnt = parseInt(parts[1].trim(), 10);
          if (dom && !isNaN(cnt) && cnt > 0) domains[dom] = cnt;
        }
      });
      config_json = {
        carryover_emails: emails.length > 0 ? emails : undefined,
        carryover_domains: Object.keys(domains).length > 0 ? domains : undefined,
        max_per_domain: carryoverMaxPerDomain ? (Number(carryoverMaxPerDomain) || undefined) : undefined,
      };
      // Strip undefined keys
      config_json = Object.fromEntries(Object.entries(config_json).filter(([, v]) => v !== undefined));
      if (Object.keys(config_json).length === 0) config_json = null;
    }
    if (checkName === 'set_check') {
      if (conditionField && conditionValues.length > 0) {
        const condition = { field: conditionField, in: conditionValues };
        if (elseRequireBlankEnabled && elseRequireBlankFields.length > 0) {
          condition.else = { require_blank: elseRequireBlankFields };
        }
        config_json = { condition };
      }
    }
    return onSave(checkName, { active, severity, config_json });
  };

  // Downloads a CSV template for the current field settings — "supplier"
  // includes every tracked tier (reject/review/optional, everything a
  // supplier needs to know about) with Program/Component/Goal ID columns
  // first, matching SupplierPortal.js's own download button; "client" is
  // just the Required — Client (reject-tier) fields, no system ID columns,
  // since it represents what actually reaches the client, not an upload
  // artifact. Same "{label} ({client alias})" compound-header convention as
  // the Supplier Portal template, so it round-trips the same way on upload.
  const downloadFieldTemplate = (fieldKeys, kind) => {
    if (fieldKeys.length === 0) return;
    const fieldMeta = Object.fromEntries((allLeadFields || []).map(f => [f.key, f]));
    const labelFor = (k) => {
      const f = fieldMeta[k];
      const label = f ? f.label : k;
      const alias = selectedFieldAliases[k];
      return alias ? `${label} (${alias})` : label;
    };
    const includeSystemCols = kind === 'supplier';
    const headers = includeSystemCols ? ['Program ID', 'Component ID', 'Goal ID'] : [];
    fieldKeys.forEach(k => headers.push(labelFor(k)));
    const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const lines = [headers.map(esc).join(',')];
    if (includeSystemCols) {
      lines.push(headers.map(h => (h === 'Program ID' ? String(resolvedProgramId || '') : '')).map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(programName || 'program').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_${kind}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Debounced auto-save — fires shortly after the last edit (markDirty()),
  // rather than waiting for an explicit Save click. Reuses handleSave's own
  // config_json-building logic via the closure, so every field change
  // (toggle, select, keyword add, band pick, list-editing textarea, …) is
  // captured the same way a manual save always was.
  useEffect(() => {
    if (!dirty) return undefined;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      handleSave().then(() => {
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
        // Program-level only — a component's own field_settings don't
        // represent "the program's" template when other components under
        // the same program may configure this differently.
        if (checkName === 'required_fields' && level === 'program' && programName) {
          const supplierKeys = Object.keys(selectedFieldSettings);
          // "Client template" = Required — Client fields, PLUS anything a live
          // destination actually references — see buildApiFieldMap()'s and
          // this same round's backend _sync_export_templates_from_required_fields()
          // docstring for why (discovered live 2026-08-07: Armis needs
          // mobile_phone/naics_code, both tier "optional", in its client
          // template because its destination's field_mapping needs them).
          const apiFieldMapForSave = buildApiFieldMap(apiDestinations, allLeadFields, selectedFieldAliases);
          const clientKeys = Object.entries(selectedFieldSettings)
            .filter(([k, t]) => t === 'reject' || (apiFieldMapForSave[k] || []).length > 0)
            .map(([k]) => k);
          syncExportTemplate(resolvedProgramId, `${programName} - supplier template`, supplierKeys, selectedFieldAliases);
          syncExportTemplate(resolvedProgramId, `${programName} - client template`, clientKeys, selectedFieldAliases);
        }
      });
    }, 600);
    return () => clearTimeout(saveTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, active, severity, selectedBands, selectedFieldSettings, selectedFieldAliases, selectedDateFormat, blankPlaceholders, keywords, autoAcceptTitles, autoRejectTitles, bypassNonUsCa, allowedAssets, maxPerDomain, carryoverEmails, carryoverDomainText, carryoverMaxPerDomain, conditionField, conditionValues, elseRequireBlankEnabled, elseRequireBlankFields]);

  const addKeyword = (text, cat, matchType) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setKeywords(prev => [...prev, { keyword: trimmed, category: cat, match_type: matchType }]);
    markDirty();
  };
  const updateKeywordCategory = (idx, cat) => {
    setKeywords(prev => prev.map((k, i) => i === idx ? { ...k, category: cat } : k));
    markDirty();
  };
  const updateKeywordMatchType = (idx, matchType) => {
    setKeywords(prev => prev.map((k, i) => i === idx ? { ...k, match_type: matchType } : k));
    markDirty();
  };
  const removeKeyword = (idx) => {
    setKeywords(prev => prev.filter((_, i) => i !== idx));
    markDirty();
  };
  const handlePasteBulk = () => {
    const lines = pasteText.split('\n').map(l => l.trim()).filter(Boolean);
    const existing = new Set(keywords.map(k => k.keyword.toLowerCase()));
    const newOnes = lines.filter(l => !existing.has(l.toLowerCase())).map(k => ({ keyword: k, category: newKwCat, match_type: newKwMatchType }));
    if (newOnes.length) { setKeywords(prev => [...prev, ...newOnes]); markDirty(); }
    setPasteText(''); setShowPaste(false);
  };

  const tlList = meta.source === 'targeting_lists' ? (targetingLists[meta.sourceKey] || []) : [];
  const inh = inherited?.[checkName];
  const hasRule = !!rule;
  const effectiveFromInherited = !hasRule && inh;
  const overlayOpen = carryoverGenOpen || Object.values(openOverlays).some(Boolean);

  // Required Fields is Program/Component-only — never goal-level.
  if (checkName === 'required_fields' && level === 'goal') {
    return (
      <div style={{ border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px', background: '#fafafa', fontSize: '12px', color: '#888' }}>
        <strong style={{ color: '#2c3e50' }}>{meta.label}</strong> is configured at the <strong>Program</strong> or <strong>Component</strong> level — select one of those above to manage it.
      </div>
    );
  }

  return (
    <div style={{
      border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px',
      background: active ? '#fff' : '#fafafa',
      // An inactive check is dimmed with opacity — but opacity < 1 makes the
      // card its own stacking context, which traps every dropdown/modal inside
      // it beneath the NEXT card on the page, whatever their z-index (real
      // production report 2026-10-09: "Connect existing list" on an
      // unconfigured Geo check showed its matches behind the Segment card,
      // faded and unclickable). So while any picker in this card is open, drop
      // the dimming and raise the whole card above its siblings.
      opacity: active || effectiveFromInherited || overlayOpen ? 1 : 0.7,
      position: 'relative',
      zIndex: overlayOpen ? 20 : 'auto',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={active} onChange={e => { setActive(e.target.checked); markDirty(); }} style={{ width: '15px', height: '15px' }} />
        </label>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>{meta.label}</span>
            {hasRule
              ? <span style={pillBadge(active ? '#1a7f4b' : '#888', active ? '#d4f5e5' : '#f0f0f0')}>{active ? 'Active' : 'Inactive'}</span>
              : effectiveFromInherited
                ? <span style={pillBadge('#7d6608', '#fef9e7')}>Inherited from {inh.level} — {inh.active ? 'Active' : 'Inactive'}</span>
                : <span style={pillBadge('#888', '#f0f0f0')}>Not configured</span>
            }
            {overridesBelow && overridesBelow.length > 0 && (
              <span
                title={`This program-level setting isn't the whole picture — a more specific rule wins wherever it's configured:\n${overridesBelow.map(o => `${o.level === 'goal' ? `Goal: ${o.entity_name}${o.component_name ? ` (${o.component_name})` : ''}` : `Component: ${o.entity_name}`} — ${o.active ? 'Active' : 'Inactive'}`).join('\n')}`}
                style={{ ...pillBadge('#a04000', '#fdf0e6'), cursor: 'help' }}
              >
                ⚠ Overridden at {overridesBelow.length} {overridesBelow.length === 1 ? 'level' : 'levels'} below
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{meta.description}</div>
        </div>

        {checkName !== 'required_fields' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: '#555' }}>Severity:</span>
            <select value={severity} onChange={e => { setSeverity(e.target.value); markDirty(); }} disabled={!active}
              style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '4px 8px', fontSize: '12px' }}>
              <option value="reject">Reject</option>
              <option value="review">Needs Review</option>
            </select>
          </div>
        )}

        <span style={{ fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap', minWidth: '70px', textAlign: 'right' }}>
          {saving
            ? <span style={{ color: '#2980b9' }}>Saving…</span>
            : dirty
              ? <span style={{ color: '#e67e22' }}>Unsaved…</span>
              : justSaved
                ? <span style={{ color: '#1a7f4b' }}>✓ Saved</span>
                : null}
        </span>
      </div>

      {/* Program level only: the actual EFFECTIVE lists in play at each
          overriding Component/Goal — not just the warning badge above, and
          not just what's assigned directly at that one level either.
          Targeting lists pool additively across goal+component+program (see
          backend qa_checks.py's load_goal_qa_context()), unlike the QARule
          itself (strictly most-granular-wins) — a component override with
          zero lists of its own can still be fully driven by the program's
          list underneath it, so `lists` here is the real union, matching
          what QA actually runs against. Only shown for list-based checks
          (the same source === 'targeting_lists' / listConnectable checks
          that get their own ListManagePanel below); other checks (phone,
          consent, etc.) still get the badge above but have no list to show
          here. */}
      {overridesBelow && overridesBelow.length > 0 && (meta.source === 'targeting_lists' || meta.listConnectable) && (
        <div style={{ marginTop: '8px', marginLeft: '26px', padding: '8px 10px', background: '#fdf6f0', border: '1px solid #f5dfc9', borderRadius: '6px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#a04000', marginBottom: '6px' }}>
            Lists in use where this is overridden:
          </div>
          {overridesBelow.map(o => (
            <div key={`${o.level}-${o.entity_id}`} style={{ fontSize: '12px', color: '#555', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '600', color: '#2c3e50' }}>
                {o.level === 'goal' ? `Goal: ${o.entity_name}${o.component_name ? ` (${o.component_name})` : ''}` : `Component: ${o.entity_name}`}
              </span>
              <span style={pillBadge(o.active ? '#1a7f4b' : '#888', o.active ? '#d4f5e5' : '#f0f0f0')}>{o.active ? 'Active' : 'Inactive'}</span>
              {o.lists && o.lists.length > 0
                ? o.lists.map(l => (
                    <span key={l.id} style={pillBadge('#2c3e50', '#eef2f7')}>{l.list_name} — {l.item_count} items</span>
                  ))
                : <span style={{ color: '#999', fontStyle: 'italic' }}>no list assigned at any level — this check has nothing to validate against</span>
              }
            </div>
          ))}
        </div>
      )}

      {/* Set Check's "only apply when [field] is one of [...]" gate — a
          reusable condition any F4/F5 QARule can carry (backend
          qa_checks.py's _evaluate_condition()), surfaced here first since
          Set Check's own state/date-format validation checks are the
          motivating case (Armis: state is only required/validated for
          Australia/Brazil/Canada/China/India/US). No condition set = the
          check always applies, today's default behavior. This is the
          whole-check version — applies to every list assigned below; a
          per-LIST version (applies to just one assigned list) lives on each
          pill in ListManagePanel. Collapsed to a compact summary/add-link by
          default (`conditionExpanded`) so the common no-condition case
          doesn't eat vertical space. */}
      {checkName === 'set_check' && (
        <div style={{ marginTop: '8px', marginLeft: '26px' }}>
          {!conditionExpanded ? (
            conditionField && conditionValues.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', padding: '5px 10px', background: '#f7f9fc', border: '1px solid #dde4ee', borderRadius: '6px', fontSize: '12px' }}>
                <span style={pillBadge('#2c5aa0', '#e4ecfc')}>⚡ condition</span>
                <span style={{ color: '#2c3e50' }}>
                  {conditionField} is one of {conditionValues.length} value{conditionValues.length !== 1 ? 's' : ''}
                  {elseRequireBlankEnabled && elseRequireBlankFields.length > 0 ? `, else ${elseRequireBlankFields.join(', ')} must be blank` : ''}
                </span>
                <span style={{ color: '#aaa', fontStyle: 'italic' }}>— applies to all lists in Set Check</span>
                <button type="button" onClick={() => setConditionExpanded(true)} style={{ background: 'none', border: 'none', color: '#2980b9', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>Edit</button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConditionExpanded(true)}
                style={{ background: 'none', border: 'none', color: '#2980b9', fontSize: '12px', cursor: 'pointer', fontWeight: '600', padding: 0 }}
              >
                + Add condition <span style={{ color: '#aaa', fontWeight: 'normal', fontStyle: 'italic' }}>(applies to all lists in Set Check)</span>
              </button>
            )
          ) : (
            <div style={{ padding: '10px 12px', background: '#f7f9fc', border: '1px solid #dde4ee', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#888', letterSpacing: '0.03em' }}>CONDITION — APPLIES TO ALL LISTS IN SET CHECK</span>
                <button type="button" onClick={() => setConditionExpanded(false)} style={{ background: 'none', border: 'none', color: '#2980b9', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>Done</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', color: '#2c3e50' }}>
                <span>Only apply this check when</span>
                <SearchableSelect
                  options={buildMatchFieldOptions(allLeadFields, conditionField)}
                  value={conditionField}
                  onChange={v => { setConditionField(v); markConditionDirtyIfComplete(v, conditionValues); }}
                  placeholder="a field…"
                />
                <span>is one of</span>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <TagInput
                    tags={conditionValues}
                    onChange={v => { setConditionValues(v); markConditionDirtyIfComplete(conditionField, v); }}
                    placeholder="Type a value + Enter…"
                  />
                </div>
                {(conditionField || conditionValues.length > 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      setConditionField('');
                      setConditionValues([]);
                      setElseRequireBlankEnabled(false);
                      setElseRequireBlankFields([]);
                      markDirty();
                    }}
                    style={{ background: 'none', border: 'none', color: '#c0392b', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Remove
                  </button>
                )}
              </div>
              {conditionField && conditionValues.length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', fontSize: '13px', color: '#2c3e50' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={elseRequireBlankEnabled}
                      onChange={e => { setElseRequireBlankEnabled(e.target.checked); markElseDirtyIfComplete(e.target.checked, elseRequireBlankFields); }}
                    />
                    Otherwise, require
                  </label>
                  {elseRequireBlankEnabled && (
                    <div style={{ minWidth: '200px' }}>
                      <MultiSelect
                        options={(allLeadFields || []).map(f => ({ value: f.key, label: f.label }))}
                        values={elseRequireBlankFields}
                        onChange={v => { setElseRequireBlankFields(v); markElseDirtyIfComplete(elseRequireBlankEnabled, v); }}
                        placeholder="a field…"
                      />
                    </div>
                  )}
                  {elseRequireBlankEnabled && <span>to be blank</span>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Targeting list management */}
      {meta.source === 'targeting_lists' && (
        <ListManagePanel
          checkName={checkName}
          listType={meta.listType}
          assignedLists={tlList}
          level={level}
          entityId={entityId}
          onChanged={onListChange}
          allLeadFields={allLeadFields}
          onOverlayChange={handleOverlayChange}
        />
      )}

      {/* List-connect for checks that also support manual/inline config — a
          connected list ADDS to whatever's typed in below, never replaces it. */}
      {meta.listConnectable && meta.listConnectable.map(lc => {
        const bucket = targetingLists?.[LIST_CONNECT_BUCKET[checkName]] || [];
        const assigned = bucket.filter(l => l.list_type === lc.listType);
        return (
          <div key={lc.listType}>
            {lc.subLabel && (
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#888', marginTop: '10px', paddingLeft: '26px' }}>{lc.subLabel}</div>
            )}
            <ListManagePanel
              checkName={checkName}
              listType={lc.listType}
              columns={lc.columns}
              assignedLists={assigned}
              level={level}
              entityId={entityId}
              onChanged={onListChange}
              onOverlayChange={handleOverlayChange}
            />
            {checkName === 'carryover_list' && lc.listType === 'Carryover Domain Counts' && (
              <div style={{ paddingLeft: '26px', marginTop: '2px' }}>
                {/* Domain counts do nothing without a max — check_carryover()
                    skips the whole domain sub-mode. Say so, rather than let
                    an attached list look like it's working. */}
                {active && assigned.length > 0 && !carryoverMaxPerDomain && (
                  <div style={{ fontSize: '11px', color: '#e67e22', marginBottom: '4px' }}>
                    ⚠ Domain counts are attached but “Combined max per domain” below is blank, so they aren’t being applied.
                  </div>
                )}
                {level && entityId && resolvedProgramId && (
                  <button onClick={() => { setCarryoverGenMsg(null); setCarryoverGenOpen(true); }}
                    title="Count a prior program's leads per domain and attach them here"
                    style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '4px', border: '1px solid #27ae60', background: '#fff', color: '#27ae60', cursor: 'pointer' }}>
                    ⚡ Generate from prior program
                  </button>
                )}
                {carryoverGenMsg && (
                  <span style={{ fontSize: '11px', color: '#1a7f4b', marginLeft: '8px' }}>{carryoverGenMsg}</span>
                )}
                {carryoverGenOpen && (
                  <CarryoverGenerateModal
                    programs={programs}
                    targetProgramId={resolvedProgramId}
                    level={level}
                    entityId={entityId}
                    defaultMax={carryoverMaxPerDomain || domainCapMax}
                    onClose={() => setCarryoverGenOpen(false)}
                    onGenerated={data => {
                      setCarryoverGenOpen(false);
                      setCarryoverGenMsg(`✓ Attached "${data.list_name}" — ${data.domain_count.toLocaleString()} domains, max ${data.rule.config_json.max_per_domain} per domain.`);
                      onListChange && onListChange();
                    }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Title keywords — Primary / Secondary / Reject table */}
      {checkName === 'title_keywords' && active && (
        <div style={{ marginTop: '12px', paddingLeft: '26px' }}>
          {/* Auto-learned titles lists — see backend app/auto_titles.py. Each
              toggle keeps its own Title Keywords list at this level, shown in
              the connected-lists panel above like any other list. */}
          <div style={{ marginBottom: '12px', padding: '8px 10px', background: '#f7f9fc', border: '1px solid #dde4ee', borderRadius: '6px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px' }}>
              <input type="checkbox" checked={autoAcceptTitles}
                onChange={e => { setAutoAcceptTitles(e.target.checked); markDirty(); }}
                style={{ marginRight: '6px' }} />
              <strong>Auto-accepted titles list</strong>
              <span style={{ color: '#888' }}> — when a lead is DemandArc or Client Accepted, add its title (exact, Secondary)</span>
            </label>
            <label style={{ display: 'block', fontSize: '12px', color: '#444', marginBottom: '4px' }}>
              <input type="checkbox" checked={autoRejectTitles}
                onChange={e => { setAutoRejectTitles(e.target.checked); markDirty(); }}
                style={{ marginRight: '6px' }} />
              <strong>Auto-rejected titles list</strong>
              <span style={{ color: '#888' }}> — when a lead is Rejected with a title flag (QA or client feedback), add its title as Reject</span>
            </label>
            <div style={{ fontSize: '11px', color: '#888' }}>
              The most recent decision wins: a title moves between the two lists if a later lead goes the other way. Your own keywords are never changed.
            </div>
            {(rule?.config_json?.auto_accept_titles || rule?.config_json?.auto_reject_titles) && level && entityId && (
              <div style={{ marginTop: '6px' }}>
                <button
                  disabled={seedingTitles}
                  onClick={() => {
                    if (!window.confirm('Add titles from leads already accepted (and title-rejected) at this level? Safe to run more than once.')) return;
                    setSeedingTitles(true); setSeedTitlesMsg(null);
                    axios.post(`${API}/qa-rules/${level}/${entityId}/title_keywords/seed-auto-titles`)
                      .then(r => {
                        const d = r.data;
                        setSeedTitlesMsg({ ok: true, text: `✓ Checked ${d.scanned} leads — added ${d.accepted_added} accepted, ${d.rejected_added} rejected${d.moved ? `, moved ${d.moved}` : ''}.` });
                        onListChange && onListChange();
                      })
                      .catch(e => setSeedTitlesMsg({ ok: false, text: e.response?.data?.detail || 'Seeding failed' }))
                      .finally(() => setSeedingTitles(false));
                  }}
                  style={{ padding: '3px 10px', fontSize: '11px', borderRadius: '4px', border: '1px solid #3498db', background: '#fff', color: '#3498db', cursor: seedingTitles ? 'default' : 'pointer', opacity: seedingTitles ? 0.6 : 1 }}
                >{seedingTitles ? 'Seeding…' : 'Seed from existing leads'}</button>
                {seedTitlesMsg && (
                  <span style={{ fontSize: '11px', marginLeft: '8px', color: seedTitlesMsg.ok ? '#1a7f4b' : '#c0392b' }}>{seedTitlesMsg.text}</span>
                )}
              </div>
            )}
          </div>
          {/* Keyword summary pill */}
          {(() => {
            const p = keywords.filter(k => k.category === 'primary').length;
            const s = keywords.filter(k => k.category === 'secondary').length;
            const r = keywords.filter(k => k.category === 'reject').length;
            if (!p && !s && !r) return null;
            return (
              <div style={{ marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', color: '#7d6608', background: '#fef9e7', borderRadius: '10px', padding: '2px 10px', border: '1px solid #f0d060' }}>
                  {p} primary · {s} secondary · {r} reject
                </span>
              </div>
            );
          })()}

          {/* Keyword table */}
          <div style={{ border: '1px solid #e0e7ef', borderRadius: '6px', overflow: 'hidden', marginBottom: '8px' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 110px 32px', gap: '0', background: '#f5f7fa', borderBottom: '1px solid #e0e7ef', padding: '6px 10px', fontSize: '11px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              <span>Keyword</span><span>Category</span><span>Match type</span><span></span>
            </div>
            {keywords.length === 0 && (
              <div style={{ padding: '12px 10px', fontSize: '12px', color: '#aaa', fontStyle: 'italic' }}>No keywords configured yet.</div>
            )}
            {keywords.map((kw, idx) => {
              const catColors = { primary: { bg: '#d4f5e5', color: '#1a7f4b' }, secondary: { bg: '#eef4ff', color: '#2c5aa0' }, reject: { bg: '#fdf0ee', color: '#c0392b' } };
              const cc = catColors[kw.category] || catColors.secondary;
              return (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 110px 32px', alignItems: 'center', borderBottom: '1px solid #f0f3f8', padding: '5px 10px', background: idx % 2 === 0 ? '#fff' : '#fafbfd' }}>
                  <span style={{ fontSize: '13px', color: '#2c3e50', padding: '2px 6px', background: cc.bg, borderRadius: '4px', display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ color: cc.color, fontWeight: '500' }}>{kw.keyword}</span>
                  </span>
                  <select
                    value={kw.category}
                    onChange={e => updateKeywordCategory(idx, e.target.value)}
                    style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '3px 6px', background: cc.bg, color: cc.color, fontWeight: '600' }}
                  >
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="reject">Reject</option>
                  </select>
                  <select
                    value={kw.match_type || 'whole_word'}
                    onChange={e => updateKeywordMatchType(idx, e.target.value)}
                    title="How this keyword is matched against the lead's title"
                    style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '3px 6px' }}
                  >
                    {MATCH_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button onClick={() => removeKeyword(idx)} title="Remove keyword"
                    style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '2px' }}>×</button>
                </div>
              );
            })}

            {/* Add keyword row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 110px 72px', gap: '6px', padding: '8px 10px', background: '#f9fafb', borderTop: keywords.length ? '1px solid #e0e7ef' : 'none' }}>
              <input
                value={newKwText}
                onChange={e => setNewKwText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(newKwText, newKwCat, newKwMatchType); setNewKwText(''); } }}
                placeholder="Add keyword…"
                style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 8px', outline: 'none' }}
              />
              <select value={newKwCat} onChange={e => setNewKwCat(e.target.value)}
                style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 6px' }}>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="reject">Reject</option>
              </select>
              <select value={newKwMatchType} onChange={e => setNewKwMatchType(e.target.value)}
                title="How this keyword is matched against the lead's title"
                style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 6px' }}>
                {MATCH_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={() => { addKeyword(newKwText, newKwCat, newKwMatchType); setNewKwText(''); }}
                style={{ fontSize: '12px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontWeight: '600' }}>
                Add
              </button>
            </div>
          </div>

          {/* Paste bulk keywords */}
          <div>
            <button onClick={() => setShowPaste(p => !p)}
              style={{ fontSize: '12px', color: '#2980b9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              {showPaste ? '▲ Hide bulk paste' : '▼ Paste bulk keyword list'}
            </button>
            {showPaste && (
              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  Paste one keyword per line. All will be added with the selected category and match type (change individually after adding).
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <textarea
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    placeholder={"keyword one\nkeyword two\nkeyword three"}
                    rows={5}
                    style={{ flex: 1, fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 8px', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <select value={newKwCat} onChange={e => setNewKwCat(e.target.value)}
                      style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 6px' }}>
                      <option value="primary">Primary</option>
                      <option value="secondary">Secondary</option>
                      <option value="reject">Reject</option>
                    </select>
                    <select value={newKwMatchType} onChange={e => setNewKwMatchType(e.target.value)}
                      title="How these keywords are matched against the lead's title"
                      style={{ fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 6px' }}>
                      {MATCH_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button onClick={handlePasteBulk}
                      style={{ fontSize: '12px', background: '#1a7f4b', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontWeight: '600' }}>
                      Add All
                    </button>
                    <button onClick={() => { setPasteText(''); setShowPaste(false); }}
                      style={{ fontSize: '11px', background: 'none', border: '1px solid #ccc', borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', color: '#888' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Domain cap */}
      {checkName === 'domain_cap' && active && (
        <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>
            Max accepted leads per email domain within this goal:
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="number"
              min="1"
              value={maxPerDomain}
              onChange={e => { setMaxPerDomain(e.target.value); markDirty(); }}
              style={{ width: '72px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
            />
            <span style={{ fontSize: '12px', color: '#666' }}>leads per domain</span>
          </div>
          {(Number(maxPerDomain) < 1 || !maxPerDomain) && (
            <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '4px' }}>⚠ Must be at least 1.</div>
          )}
        </div>
      )}

      {/* Address Format — skip leads outside the US/Canada (default on) */}
      {checkName === 'address' && active && (
        <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
          <label style={{ fontSize: '12px', color: '#444' }}>
            <input type="checkbox" checked={bypassNonUsCa}
              onChange={e => { setBypassNonUsCa(e.target.checked); markDirty(); }}
              style={{ marginRight: '6px' }} />
            Bypass check if country is not US or Canada
          </label>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
            Addresses outside the US and Canada often don’t start with a street number. Leads with no country are still checked.
          </div>
        </div>
      )}

      {/* Carryover list */}
      {checkName === 'carryover_list' && active && (
        <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
          {/* Email carryover */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
              Prior-phase email list <span style={{ fontWeight: 400, color: '#888' }}>(one per line — leads with a matching email will be flagged)</span>
            </div>
            <textarea
              value={carryoverEmails}
              onChange={e => { setCarryoverEmails(e.target.value); markDirty(); }}
              placeholder="email1@company.com&#10;email2@company.com&#10;…"
              rows={5}
              style={{ width: '100%', maxWidth: '460px', fontSize: '12px', fontFamily: 'monospace', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
            />
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              {carryoverEmails.split('\n').filter(e => e.trim()).length} email{carryoverEmails.split('\n').filter(e => e.trim()).length !== 1 ? 's' : ''} entered
            </div>
          </div>
          {/* Domain carryover counts */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
              Prior-phase domain counts <span style={{ fontWeight: 400, color: '#888' }}>(domain[tab or comma]count, one per line)</span>
            </div>
            <textarea
              value={carryoverDomainText}
              onChange={e => { setCarryoverDomainText(e.target.value); markDirty(); }}
              placeholder="microsoft.com&#9;3&#10;salesforce.com&#9;2&#10;…"
              rows={4}
              style={{ width: '100%', maxWidth: '360px', fontSize: '12px', fontFamily: 'monospace', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box', resize: 'vertical' }}
            />
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              {carryoverDomainText.split('\n').filter(l => l.trim().split(/[\t,]+/).length >= 2).length} domain{carryoverDomainText.split('\n').filter(l => l.trim().split(/[\t,]+/).length >= 2).length !== 1 ? 's' : ''} entered
            </div>
            <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: '#555' }}>Combined max per domain:</span>
              <input
                type="number"
                min="1"
                value={carryoverMaxPerDomain}
                onChange={e => { setCarryoverMaxPerDomain(e.target.value); markDirty(); }}
                placeholder="e.g. 6"
                style={{ width: '72px', padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
              />
              <span style={{ fontSize: '11px', color: '#888' }}>(prior count + new leads ≤ this)</span>
            </div>
          </div>
        </div>
      )}

      {/* Asset alignment */}
      {/* Required fields — grouped checklist, same format as the Export Settings field picker */}
      {checkName === 'required_fields' && active && (() => {
        const grouped = [];
        // `export_only` fields (Program/Component/Goal Name) are derived names a
        // PM can include in an export, not supplier-supplied columns — nothing to
        // require, so they're not offered here.
        (allLeadFields || []).filter(f => !f.export_only).forEach(f => {
          let entry = grouped.find(([g]) => g === f.group);
          if (!entry) { entry = [f.group, []]; grouped.push(entry); }
          entry[1].push(f);
        });
        const counts = { reject: 0, review: 0, optional: 0 };
        Object.values(selectedFieldSettings).forEach(t => { if (counts[t] !== undefined) counts[t]++; });
        // Fixed (hardcoded) target-field values from this program's active
        // delivery destination(s) — these don't come from any Lead field, so
        // they can't attach to a checklist row; surfaced once here instead.
        const fixedValueEntries = [];
        (apiDestinations || []).forEach(d => {
          Object.entries(d.fixed_values || {}).forEach(([targetField, literal]) => {
            if (literal !== '' && literal != null) {
              fixedValueEntries.push({ targetField, literal, destinationName: d.name || d.connection_name || `Destination ${d.id}` });
            }
          });
        });
        // One field per row, in a real aligned table rather than a wrapping
        // multi-column grid — with an API column, each row now carries 4
        // distinct pieces of info (tier, name, client alias, API field), which
        // stopped fitting legibly as flex items packed into narrow auto-fill
        // cells. The API column only renders when this program actually has
        // a destination — most programs won't, and an all-empty column would
        // just be noise.
        const hasApi = (apiDestinations || []).length > 0;
        // The client-name column gets most of the spare width: it was a fixed 160px (a long
        // alias like "Custom Field 1 (Company HQ State)" was cut off) while the short field
        // label next to it took all the slack.
        const gridTemplate = hasApi
          ? '110px minmax(140px, 1fr) minmax(300px, 2fr) 170px'
          : '110px minmax(140px, 1fr) minmax(300px, 2fr)';
        // Computed once across the whole field list (not per row) — see
        // buildApiFieldMap()'s comment for why that matters.
        const apiFieldMap = hasApi ? buildApiFieldMap(apiDestinations, allLeadFields, selectedFieldAliases) : {};
        // Read-only drift check — see buildFieldDrift()'s own comment. Cheap
        // to compute even when nothing's wired up yet (empty maps in, empty
        // map out), so it isn't gated behind hasApi the way apiFieldMap is.
        const fieldDrift = buildFieldDrift(apiFieldMap, selectedFieldSettings, selectedFieldAliases, apiExportConfigs, programName);
        const supplierKeys = Object.keys(selectedFieldSettings);
        // Same expanded rule as the auto-save sync above — Required — Client
        // fields plus anything a live destination actually references.
        const clientKeys = Object.entries(selectedFieldSettings)
          .filter(([k, t]) => t === 'reject' || (apiFieldMap[k] || []).length > 0)
          .map(([k]) => k);
        return (
          <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
              <div style={{ fontSize: '12px', color: '#555', fontWeight: '600' }}>
                {counts.reject || counts.review || counts.optional
                  ? `${counts.reject} Required — Client · ${counts.review} Required — Supplier · ${counts.optional} Optional`
                  : '— nothing tracked, check will pass every lead'}:
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => downloadFieldTemplate(supplierKeys, 'supplier')}
                  disabled={supplierKeys.length === 0}
                  title="CSV with every tracked field (Required — Client, Required — Supplier, and Optional) plus Program/Component/Goal ID columns — for the supplier to fill in and upload."
                  style={{ padding: '4px 10px', background: 'white', border: '1px solid #3498db', color: supplierKeys.length ? '#1a6da8' : '#ccc', borderRadius: '4px', cursor: supplierKeys.length ? 'pointer' : 'not-allowed', fontSize: '11px', fontWeight: '600' }}>
                  ⬇ Download Supplier Template
                </button>
                <button
                  onClick={() => downloadFieldTemplate(clientKeys, 'client')}
                  disabled={clientKeys.length === 0}
                  title="CSV with just the Required — Client fields — what actually reaches the client on delivery."
                  style={{ padding: '4px 10px', background: 'white', border: '1px solid #7d3c98', color: clientKeys.length ? '#7d3c98' : '#ccc', borderRadius: '4px', cursor: clientKeys.length ? 'pointer' : 'not-allowed', fontSize: '11px', fontWeight: '600' }}>
                  ⬇ Download Client Template
                </button>
              </div>
            </div>
            {level === 'program' && (supplierKeys.length > 0 || clientKeys.length > 0) && (
              <div style={{ fontSize: '11px', color: '#8a96a3', marginBottom: '8px' }}>
                Auto-saved to Export Configs as "{programName} - supplier template" and "{programName} - client template" for use when exporting leads.
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '12px', color: '#555', fontWeight: '600' }}>Date Format</span>
              <select
                value={selectedDateFormat}
                onChange={e => { setSelectedDateFormat(e.target.value); markDirty(); }}
                style={{ padding: '3px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}>
                <option value="">Default ({defaultDateFormat || 'MM/DD/YYYY'})</option>
                {DATE_FORMAT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{ fontSize: '11px', color: '#888' }} title="This client's own date convention for automated sends (Client Delivery Wizard and CSV delivery) — separate from a manual export download's own date format.">
                for automated sends (Wizard / CSV delivery) ⓘ
              </span>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '12px', color: '#555', cursor: 'pointer' }}
              title={`When leads are uploaded, a value that is only ${PLACEHOLDER_EXAMPLES} (or a dash) is treated as blank, so Required Fields flags it as missing instead of passing it through to the client. First and last names keep words like "None" or "Na". Turn this off only if this client explicitly wants "-" or "N/A" kept.`}>
              <input type="checkbox" checked={blankPlaceholders}
                onChange={e => { setBlankPlaceholders(e.target.checked); markDirty(); }} />
              <span style={{ fontWeight: '600' }}>Treat "-", "N/A" and similar placeholders as blank on upload</span>
              <span style={{ fontSize: '11px', color: '#888' }}>
                {blankPlaceholders ? 'on (default)' : 'off: placeholder values are kept as typed'} ⓘ
              </span>
            </label>

            {!aliasUploadHeaders && (
              <div
                onDragOver={e => { e.preventDefault(); setAliasUploadDragOver(true); }}
                onDragLeave={() => setAliasUploadDragOver(false)}
                onDrop={handleAliasDrop}
                onClick={() => aliasFileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${aliasUploadDragOver ? '#1a6da8' : '#ccc'}`, borderRadius: '6px',
                  padding: '10px 12px', textAlign: 'center', cursor: 'pointer', marginBottom: '8px',
                  background: aliasUploadDragOver ? '#eaf4fb' : '#fafbfc', color: '#666', fontSize: '12px',
                }}
              >
                📎 Drag & drop a client's CSV/XLSX mapping template here, or click to browse — we'll match its column headers to fields and let you review before filling in Client Alias.
                <input ref={aliasFileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleAliasFileInputChange} style={{ display: 'none' }} />
              </div>
            )}
            {aliasUploadError && (
              <div style={{ fontSize: '11px', color: '#c0392b', marginBottom: '8px' }}>{aliasUploadError}</div>
            )}
            {aliasUploadHeaders && (
              <div style={{ border: '1px solid #e4e9ef', borderRadius: '4px', padding: '10px', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '12px' }}>Review mapping — {aliasUploadFileName}</div>
                  <button onClick={discardAliasUpload} style={{ fontSize: '11px', padding: '3px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', background: '#fff' }}>Upload a different file</button>
                </div>
                {aliasHasDuplicateMapping && (
                  <div style={{ background: '#fef9e7', color: '#7d6608', border: '1px solid #f0dca0', borderRadius: '4px', padding: '5px 8px', marginBottom: '8px', fontSize: '11px' }}>
                    Two or more columns are mapped to the same field — only the first will be kept when applied.
                  </div>
                )}
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  {aliasUploadHeaders.map(h => {
                    const guess = bestFieldMatch(h, allLeadFields || []);
                    return (
                      <div key={h} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: '8px', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px' }} title={h}>{h}</span>
                        <select
                          value={aliasMapping[h] || ''}
                          onChange={e => setAliasMapping(prev => ({ ...prev, [h]: e.target.value }))}
                          style={{ fontSize: '12px', padding: '3px 4px' }}
                        >
                          <option value="">— skip this column —</option>
                          {(allLeadFields || []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                        <span style={{ fontSize: '10px', color: !aliasMapping[h] ? '#aaa' : guess ? '#1a7f4b' : '#888' }}>
                          {!aliasMapping[h] ? '—' : guess && guess.field.key === aliasMapping[h] ? confidenceLabel(guess.score) : 'Manual'}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button
                    onClick={applyAliasMapping}
                    disabled={aliasMappedCount === 0}
                    style={{ fontSize: '11px', padding: '6px 14px', cursor: aliasMappedCount ? 'pointer' : 'not-allowed', border: 'none', borderRadius: '3px', fontWeight: 600, background: aliasMappedCount ? '#1a7f4b' : '#e8edf2', color: aliasMappedCount ? '#fff' : '#aaa' }}
                  >
                    Apply Aliases ({aliasMappedCount} field{aliasMappedCount === 1 ? '' : 's'})
                  </button>
                  <button onClick={discardAliasUpload} style={{ fontSize: '11px', padding: '6px 14px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '3px', background: '#fff' }}>Cancel</button>
                </div>
              </div>
            )}

            {fixedValueEntries.length > 0 && (
              <div style={{ fontSize: '11px', color: '#555', marginBottom: '8px', background: '#f7f9fb', border: '1px solid #e4e9ef', borderRadius: '4px', padding: '6px 10px' }}>
                <strong style={{ color: '#7d3c98' }}>Hardcoded API values</strong> (sent to the destination regardless of upload data):{' '}
                {fixedValueEntries.map((e, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <code style={{ background: '#eee', padding: '0 3px', borderRadius: '2px' }}>{e.targetField}</code> = "{String(e.literal)}"
                    {apiDestinations.length > 1 && <span style={{ color: '#aaa' }}> ({e.destinationName})</span>}
                  </span>
                ))}
              </div>
            )}
            <div style={{ border: '1px solid #e4e9ef', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: '10px', padding: '6px 10px', background: '#f4f6f8', borderBottom: '1px solid #e4e9ef', fontSize: '10px', fontWeight: '700', color: '#8a96a3', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                <span>Tier</span>
                <span>Field</span>
                <span>Client Alias</span>
                {hasApi && <span>API Mapping</span>}
              </div>
              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                {grouped.map(([group, groupFields]) => (
                  <div key={group}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#eef3f8', borderBottom: '1px solid #e4e9ef', fontWeight: 600, color: '#1a6da8', fontSize: '12px' }}>
                      <span>{group}</span>
                      <select
                        onChange={e => { if (e.target.value === '__clear__') setGroupTier(groupFields, ''); else if (e.target.value) setGroupTier(groupFields, e.target.value); e.target.value = ''; }}
                        defaultValue=""
                        title="Apply a tier to every field in this group"
                        style={{ fontSize: '11px', padding: '1px 4px', border: '1px solid #ccc', borderRadius: '3px', color: '#555' }}
                      >
                        <option value="" disabled>Set group…</option>
                        {TIER_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        <option value="__clear__">Clear group</option>
                      </select>
                    </div>
                    {groupFields.map((f, i) => {
                      const tier = selectedFieldSettings[f.key] || '';
                      const aliases = f.aliases || [];
                      const clientAlias = selectedFieldAliases[f.key] || '';
                      const apiHits = apiFieldMap[f.key] || [];
                      const driftMsgs = fieldDrift[f.key] || [];
                      return (
                        <div key={f.key} style={{
                          display: 'grid', gridTemplateColumns: gridTemplate, gap: '10px', alignItems: 'center',
                          padding: '5px 10px', fontSize: '13px',
                          background: i % 2 ? '#fbfcfd' : '#fff',
                          borderBottom: '1px solid #f0f2f5',
                        }}>
                          <select value={tier} onChange={e => setFieldTier(f.key, e.target.value)}
                            style={{ fontSize: '11px', padding: '3px 4px', border: '1px solid #ccc', borderRadius: '3px', color: tier ? '#2c3e50' : '#aaa', width: '100%' }}>
                            {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                            {aliases.length > 0 && (
                              <span
                                title={`Upload files can also use these column headers for this field: ${aliases.join(', ')}`}
                                style={{ color: '#8ba7c4', fontSize: '11px', cursor: 'help', flexShrink: 0 }}
                              >
                                ⓘ
                              </span>
                            )}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                            <input
                              type="text"
                              value={clientAlias}
                              onChange={e => setFieldAlias(f.key, e.target.value)}
                              placeholder="Client name…"
                              title="This client's own name for this field — uploads for this program/component will also accept it as a column header, alongside the standard aliases above."
                              style={{ width: '100%', boxSizing: 'border-box', fontSize: '11px', padding: '3px 6px', border: '1px solid #ccc', borderRadius: '3px', color: '#2c3e50' }}
                            />
                            {showsDateConversion(f.key, selectedFieldDateFormats[f.key]) && (
                              <select
                                value={conversionToValue(selectedFieldDateFormats[f.key])}
                                onChange={e => setFieldDateFormat(f.key, e.target.value)}
                                title="If the supplier sends this field as a date in a different format than the client needs, pick how to convert it. Applied when a delivery is uploaded; text that doesn't fit the chosen format is left as sent."
                                style={{ fontSize: '10px', padding: '3px 2px', border: '1px solid #ccc', borderRadius: '3px', maxWidth: '96px', flexShrink: 0, color: selectedFieldDateFormats[f.key] ? '#2c3e50' : '#aaa', background: selectedFieldDateFormats[f.key] ? '#eaf4fb' : '#fff' }}
                              >
                                {conversionOptions(selectedFieldDateFormats[f.key]).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            )}
                            {driftMsgs.length > 0 && (
                              <span
                                title={`Out of sync with what's actually wired up:\n${driftMsgs.join('\n')}`}
                                style={{ color: '#c0392b', fontSize: '13px', cursor: 'help', flexShrink: 0 }}
                              >
                                ⚠
                              </span>
                            )}
                          </span>
                          {hasApi && (
                            apiHits.length > 0 ? (
                              <span
                                title={`Sent to the API destination as: ${apiHits.map(h => `${h.targetField}${apiDestinations.length > 1 ? ` (${h.destinationName})` : ''}`).join(', ')}`}
                                style={{ fontSize: '10px', fontWeight: '600', padding: '2px 6px', borderRadius: '3px', background: '#eafaf1', color: '#1e8449', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'help', width: 'fit-content', maxWidth: '100%' }}
                              >
                                API: {apiHits.map(h => h.targetField).join(', ')}
                              </span>
                            ) : <span style={{ color: '#ccc', fontSize: '11px' }}>—</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {checkName === 'asset_alignment' && active && (
        <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>
            Allowed asset titles — asset_downloaded must exactly match one of these:
          </div>
          <TagInput tags={allowedAssets} onChange={v => { setAllowedAssets(v); markDirty(); }} placeholder="Paste list (one per line) or type + Enter…" tagColor="#7d3c98" tagBg="#f5eef8" splitOnComma={false} />
          {allowedAssets.length === 0 && <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '4px' }}>⚠ No assets configured — check will pass for all assets until at least one is added.</div>}
        </div>
      )}

      {/* Employee size bands */}
      {checkName === 'employee_size' && active && (
        <div style={{ marginTop: '10px', paddingLeft: '26px' }}>
          <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px', fontWeight: '600' }}>
            Allowed size bands {selectedBands.length > 0 ? `(${selectedBands.length} selected)` : '— select at least one to enforce'}:
          </div>

          {/* Standard bands — checkboxes */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {sizeBands.map(band => {
              const sel = selectedBands.includes(band);
              return (
                <label key={band} style={{
                  display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '14px',
                  cursor: 'pointer', fontSize: '12px', border: '1px solid', userSelect: 'none',
                  borderColor: sel ? '#2980b9' : '#ccc', background: sel ? '#eef4ff' : '#fafafa', color: sel ? '#2c5aa0' : '#555',
                }}>
                  <input type="checkbox" checked={sel} onChange={() => handleToggleBand(band)} style={{ margin: 0 }} />
                  {band}
                </label>
              );
            })}
          </div>

          {/* Custom bands */}
          <div style={{ marginTop: '10px' }}>
            <div style={{ fontSize: '11px', color: '#888', fontWeight: '600', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Custom bands</div>
            {/* Existing custom bands as removable chips */}
            {selectedBands.filter(b => !sizeBands.includes(b)).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '6px' }}>
                {selectedBands.filter(b => !sizeBands.includes(b)).map(band => (
                  <span key={band} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '3px 8px 3px 10px', borderRadius: '14px', fontSize: '12px',
                    background: '#fef9e7', border: '1px solid #f0c040', color: '#7d6608',
                  }}>
                    {band}
                    <button
                      type="button"
                      onClick={() => { setSelectedBands(prev => prev.filter(b => b !== band)); markDirty(); }}
                      style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: '0 2px' }}
                    >×</button>
                  </span>
                ))}
              </div>
            )}
            {/* Input to add a custom band */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="text"
                value={customBandInput}
                onChange={e => setCustomBandInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = customBandInput.trim();
                    if (val && !selectedBands.includes(val)) { setSelectedBands(prev => [...prev, val]); markDirty(); }
                    setCustomBandInput('');
                  }
                }}
                placeholder="e.g. 100-199"
                style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', width: '140px' }}
              />
              <button
                type="button"
                onClick={() => {
                  const val = customBandInput.trim();
                  if (val && !selectedBands.includes(val)) { setSelectedBands(prev => [...prev, val]); markDirty(); }
                  setCustomBandInput('');
                }}
                style={{ padding: '4px 12px', border: 'none', borderRadius: '4px', background: '#2980b9', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
              >Add</button>
            </div>
            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
              Enter any range (e.g. 100-199, 200-749). When a lead has an <strong>Actual Co. Size</strong> numeric value, it is validated against these ranges. Otherwise the lead's band label is matched by text.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CloneQaRulesRow — same-level clone (program-from-program, component-from-
// component, goal-from-goal). Copies every QARule at this level for the
// source entity, its direct targeting list assignments (geo/segment/
// industry/tal/suppression), and — Program level only — force_full_qa_on_upload,
// onto the currently-selected entity. Overwrites anything already configured
// on the target, so it's guarded by a confirm. Never crosses levels (a
// component never clones from a program or goal) — there's no reliable
// mapping between different levels' rules.
// ---------------------------------------------------------------------------
const CLONE_LEVEL_LABELS = { program: 'Program', component: 'Component', goal: 'Goal' };

function CloneQaRulesRow({ level, entityId, sourceCandidates, onCloned, showToast }) {
  const [sourceId, setSourceId] = useState('');
  const [cloning, setCloning] = useState(false);

  const levelLabel = CLONE_LEVEL_LABELS[level] || level;

  const sourceOptions = (sourceCandidates || [])
    .filter(e => String(e.id) !== String(entityId))
    .map(e => ({ value: String(e.id), label: e.label }));

  const handleClone = async () => {
    if (!sourceId) return;
    const sourceName = (sourceCandidates.find(e => String(e.id) === sourceId) || {}).label || `that ${levelLabel.toLowerCase()}`;
    const scopeNote = level === 'program'
      ? "targeting list assignments, and upload behavior"
      : "and targeting list assignments";
    if (!window.confirm(
      `This will overwrite this ${levelLabel.toLowerCase()}'s QA rules, ${scopeNote} with ${sourceName}'s settings. Continue?`
    )) {
      return;
    }
    setCloning(true);
    try {
      await axios.post(`${API}/qa-rules/${level}/${entityId}/clone-from/${sourceId}`);
      showToast(`Cloned QA rules from ${sourceName}.`);
      setSourceId('');
      onCloned();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to clone QA rules', 'error');
    } finally {
      setCloning(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>Clone From Another {levelLabel}</span>
      </div>
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', marginBottom: '8px' }}>
        Copy {levelLabel}-level QA rules and targeting list assignments (Geo/Segment/Industry/TAL{level === 'program' ? '/Suppression' : ''})
        {level === 'program' ? ', and Force Full QA on Upload,' : ''} from another {levelLabel.toLowerCase()} — useful when setting up a
        new {levelLabel.toLowerCase()} for a repeat client order. Overwrites this {levelLabel.toLowerCase()}'s current settings; add any
        modifications afterward.
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '260px' }}>
          <SearchableSelect
            label={`Source ${levelLabel.toLowerCase()}`}
            options={sourceOptions}
            value={sourceId}
            onChange={setSourceId}
            placeholder={`Select a ${levelLabel.toLowerCase()} to clone from…`}
          />
        </div>
        <button
          onClick={handleClone}
          disabled={!sourceId || cloning}
          style={{
            padding: '7px 14px', borderRadius: '5px', border: '1px solid #2c3e50',
            background: !sourceId || cloning ? '#eee' : '#2c3e50', color: !sourceId || cloning ? '#999' : '#fff',
            fontSize: '13px', cursor: !sourceId || cloning ? 'default' : 'pointer',
          }}
        >
          {cloning ? 'Cloning…' : 'Clone Settings'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SuppressionRow — program-only, always-active check with no QARule (no
// active/severity toggle) — just the assigned-list management UI, since
// suppression lists are scoped to a program directly (not inherited from
// goal/component like the F4 checks above).
// ---------------------------------------------------------------------------
function SuppressionRow({ level, entityId, assignedLists, globalLists, onListChange }) {
  if (level !== 'program') {
    return (
      <div style={{
        border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px',
        background: '#fafafa', fontSize: '12px', color: '#888',
      }}>
        Suppression lists are configured at the <strong>Program</strong> level — select a Program above to manage them.
      </div>
    );
  }
  return (
    <div style={{ border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>Suppression List</span>
        <span style={pillBadge('#c0392b', '#fdf0ee')}>Reject</span>
      </div>
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
        Flag leads whose email or domain is on a suppression list assigned to this program.
      </div>
      {globalLists && globalLists.length > 0 && (
        <div style={{ fontSize: '11px', color: '#7d6608', background: '#fef9e7', border: '1px solid #f0d060', borderRadius: '4px', padding: '4px 8px', marginTop: '8px', display: 'inline-block' }}>
          Also always active: {globalLists.map(g => `${g.list_name} (${g.item_count} items)`).join(', ')}
        </div>
      )}
      <ListManagePanel
        checkName="suppression"
        listType="Suppression"
        assignedLists={assignedLists}
        level={level}
        entityId={entityId}
        onChanged={onListChange}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DncRow — global-only, informational (no QARule, no program assignment at
// all, unlike SuppressionRow above). A phone/mobile_phone match against one
// of these lists is enforced absolutely server-side (Lead.status locked to
// "Rejected" forever, no override possible — app/compliance_guard.py), same
// as a globally-suppressed email; there's nothing to configure here per
// program/component/goal, so this renders identically at every level. Manage
// the list itself (add/remove numbers) from List Management.
// ---------------------------------------------------------------------------
function DncRow({ globalLists }) {
  return (
    <div style={{ border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>Do-Not-Call (DNC) List</span>
        <span style={pillBadge('#c0392b', '#fdf0ee')}>Reject — no override</span>
      </div>
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
        A lead whose phone or mobile phone is on the global DNC list is locked to Rejected permanently — this always applies, company-wide, and cannot be overridden.
      </div>
      {globalLists && globalLists.length > 0 ? (
        <div style={{ fontSize: '11px', color: '#7d6608', background: '#fef9e7', border: '1px solid #f0d060', borderRadius: '4px', padding: '4px 8px', marginTop: '8px', display: 'inline-block' }}>
          Always active: {globalLists.map(g => `${g.list_name} (${g.item_count} numbers)`).join(', ')}
        </div>
      ) : (
        <div style={{ fontSize: '11px', color: '#888', marginTop: '8px' }}>No global DNC list found.</div>
      )}
      <div style={{ marginTop: '8px' }}>
        <a href="/targeting-lists" style={{ fontSize: '12px', color: '#3498db' }}>↗ Manage numbers in List Management</a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ForceFullQaRow — program-only, plain Program.force_full_qa_on_upload field
// (not a QARule — no config_json, no inheritance, no active/severity toggle).
// Saves directly against PUT /programs/{id} rather than the qa-rules endpoints.
// ---------------------------------------------------------------------------
function ForceFullQaRow({ level, program, onSaved, showToast }) {
  const [saving, setSaving] = useState(false);

  if (level !== 'program') {
    return (
      <div style={{
        border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px',
        background: '#fafafa', fontSize: '12px', color: '#888',
      }}>
        Full QA on upload is configured at the <strong>Program</strong> level — select a Program above to change it.
      </div>
    );
  }
  if (!program) return null;

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/programs/${program.id}`, { force_full_qa_on_upload: checked });
      onSaved(res.data);
      showToast('Saved.');
    } catch (err) {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ border: '1px solid #e0e7ef', borderRadius: '6px', padding: '14px 18px', marginBottom: '10px', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>Force Full QA on Upload</span>
      </div>
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', marginBottom: '8px' }}>
        Run all QA checks (not just required fields) immediately when a supplier uploads, instead of waiting
        until a PM accepts the delivery — makes the supplier's pass/fail feedback accurate right away. A lead
        that passes still lands in Pending Review either way; this only changes when the checks run.
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', cursor: saving ? 'default' : 'pointer' }}>
        <input type="checkbox" checked={!!program.force_full_qa_on_upload} disabled={saving}
          onChange={e => handleToggle(e.target.checked)} />
        {saving ? 'Saving…' : (program.force_full_qa_on_upload ? 'On' : 'Off')}
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function QARules() {
  const [programs, setPrograms] = useState([]);
  const [components, setComponents] = useState([]);
  const [goals, setGoals] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');
  const [qaContext, setQaContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingCheck, setSavingCheck] = useState(null);
  const [toast, setToast] = useState(null);
  // Master Lead field list for the Required Fields checklist (F2) — same
  // endpoint TargetingLists.js already uses for its Match Field dropdown.
  const [allLeadFields, setAllLeadFields] = useState([]);

  // Derive level and entity from selections — most granular wins
  const level = selectedGoal ? 'goal' : selectedComponent ? 'component' : selectedProgram ? 'program' : null;
  const entityId = selectedGoal || selectedComponent || selectedProgram;

  // All raw data loaded once on mount
  const [allComponents, setAllComponents] = useState([]);
  const [allGoals, setAllGoals] = useState([]);

  // Lead Delivery destinations for the owning program (regardless of level —
  // component/goal both roll up to one program) — powers Required Fields'
  // "API Mapping" column, showing what this client's delivery destination(s)
  // expect a field to be called, alongside the NeTRA name and client alias.
  const resolvedProgramId = selectedProgram || (selectedComponent && allComponents.find(c => String(c.id) === String(selectedComponent))?.program_id) || null;
  const resolvedProgramName = resolvedProgramId ? (programs.find(p => String(p.id) === String(resolvedProgramId))?.program_name || null) : null;
  const [apiDestinations, setApiDestinations] = useState([]);
  // Read-only, for the Required Fields drift check below — the program's own
  // saved export configs (mainly the auto-generated "{program} - client
  // template", but any of them could theoretically be misnamed/reused, so the
  // check below looks it up by name rather than assuming index 0).
  const [apiExportConfigs, setApiExportConfigs] = useState([]);

  // Closed programs nest into a collapsible section on the Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  // Same treatment for Component/Goal, inherited from their owning program.
  const [programStatuses, setProgramStatuses] = useState([]);
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const programOptionGroups = buildProgramOptionGroups(programs, programStatusGroupMap);
  const closedProgramIds = closedProgramIdSet(programs, programStatusGroupMap);
  const componentByIdForGoals = new Map(allComponents.map(c => [c.id, c]));
  const componentOptionGroups = buildComponentOptionGroups(components, closedProgramIds);
  const goalOptionGroups = buildGoalOptionGroups(goals, componentByIdForGoals, closedProgramIds);

  useEffect(() => {
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data || [])).catch(() => {});
    axios.get(`${API}/components/`).then(r => { setAllComponents(r.data || []); setComponents(r.data || []); }).catch(() => {});
    axios.get(`${API}/goals/`).then(r => { setAllGoals(r.data || []); setGoals(r.data || []); }).catch(() => {});
    axios.get(`${API}/leads/export-fields`).then(r => setAllLeadFields(r.data || [])).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then(r => setProgramStatuses(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!resolvedProgramId) { setApiDestinations([]); setApiExportConfigs([]); return; }
    axios.get(`${API}/lead-delivery/destinations`, { params: { program_id: resolvedProgramId } })
      .then(r => setApiDestinations((r.data || []).filter(d => d.active)))
      .catch(() => setApiDestinations([]));
    axios.get(`${API}/export-configs/`, { params: { program_id: resolvedProgramId } })
      .then(r => setApiExportConfigs(r.data || []))
      .catch(() => setApiExportConfigs([]));
  }, [resolvedProgramId]);

  // Cascade: when program selected, filter components + goals
  useEffect(() => {
    if (!selectedProgram) {
      setComponents(allComponents);
      setGoals(allGoals);
      return;
    }
    const filteredComps = allComponents.filter(c => String(c.program_id) === String(selectedProgram));
    setComponents(filteredComps);
    const compIds = new Set(filteredComps.map(c => String(c.id)));
    setGoals(allGoals.filter(g => compIds.has(String(g.component_id))));
    // Clear child selections if they no longer belong to this program
    if (selectedComponent && !filteredComps.find(c => String(c.id) === String(selectedComponent))) {
      setSelectedComponent('');
      setSelectedGoal('');
    }
  }, [selectedProgram, allComponents, allGoals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cascade: when component selected, filter goals
  useEffect(() => {
    if (!selectedComponent) {
      // Only reset to program-filtered list if a program is selected
      if (selectedProgram) {
        const compIds = new Set(allComponents.filter(c => String(c.program_id) === String(selectedProgram)).map(c => String(c.id)));
        setGoals(allGoals.filter(g => compIds.has(String(g.component_id))));
      } else {
        setGoals(allGoals);
      }
      return;
    }
    setGoals(allGoals.filter(g => String(g.component_id) === String(selectedComponent)));
    if (selectedGoal && !allGoals.filter(g => String(g.component_id) === String(selectedComponent)).find(g => String(g.id) === String(selectedGoal))) {
      setSelectedGoal('');
    }
  }, [selectedComponent, allComponents, allGoals]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearSelections = () => {
    setSelectedProgram(''); setSelectedComponent(''); setSelectedGoal('');
    setComponents(allComponents); setGoals(allGoals); setQaContext(null);
  };

  // Load QA context whenever effective level/entity changes. `silent` skips
  // the page-level loading flag — used when a list is connected/unassigned
  // from within an already-rendered check row, so re-fetching the updated
  // targeting_summary doesn't blank the whole rules panel out to "Loading
  // rules…" and back; the only thing that visually needs to update is that
  // one check's list pills, once the new data arrives.
  const loadContext = useCallback((opts = {}) => {
    const { silent = false } = opts;
    if (!level || !entityId) { setQaContext(null); return; }
    if (!silent) setLoading(true);
    axios.get(`${API}/qa-rules/`, { params: { level, entity_id: entityId } })
      .then(r => setQaContext(r.data))
      .catch(() => showToast('Failed to load QA rules', 'error'))
      .finally(() => { if (!silent) setLoading(false); });
  }, [level, entityId]);

  const loadContextSilently = useCallback(() => loadContext({ silent: true }), [loadContext]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ForceFullQaRow's PUT /programs/{id} returns the full updated ProgramOut —
  // patch it into the already-fetched programs list in place rather than
  // refetching the whole list.
  const handleProgramSaved = (updatedProgram) => {
    setPrograms(prev => prev.map(p => (p.id === updatedProgram.id ? updatedProgram : p)));
  };

  const handleSave = async (checkName, body) => {
    setSavingCheck(checkName);
    try {
      const res = await axios.put(`${API}/qa-rules/${level}/${entityId}/${checkName}`, body);
      // No success toast here — saves now happen automatically as the PM edits
      // (see CheckRow's debounced auto-save), and a toast on every debounced
      // save would be noisy. Each row shows its own "✓ Saved" inline instead.
      // Patch only this check in qaContext — avoids a full reload that would reset
      // unsaved state in every other check card.
      setQaContext(prev => {
        if (!prev) return prev;
        const rules = prev.rules || [];
        const exists = rules.some(r => r.check_name === checkName);
        const updatedRules = exists
          ? rules.map(r => r.check_name === checkName ? res.data : r)
          : [...rules, res.data];
        return { ...prev, rules: updatedRules };
      });
      // Turning an auto-titles toggle on creates its list server-side —
      // refresh the connected-lists summary so it shows up right away
      // (the same silent reload list changes already use).
      const cfg = body?.config_json || {};
      const prevCfg = ((qaContext?.rules || []).find(r => r.check_name === checkName) || {}).config_json || {};
      const newlyOn = (cfg.auto_accept_titles && !prevCfg.auto_accept_titles) || (cfg.auto_reject_titles && !prevCfg.auto_reject_titles);
      if (checkName === 'title_keywords' && newlyOn) {
        loadContextSilently();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Save failed', 'error');
    } finally {
      setSavingCheck(null);
    }
  };

  const ruleMap = {};
  if (qaContext?.rules) qaContext.rules.forEach(r => { ruleMap[r.check_name] = r; });

  return (
    <div style={{ maxWidth: '900px' }}>
      <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', color: '#2c3e50' }}>Campaign QA Rules</h2>
      <p style={{ margin: '0 0 20px 0', color: '#7f8c8d', fontSize: '13px' }}>
        Configure QA rules at the Program, Component, or Goal level. The most granular rule takes precedence:
        <strong> Goal → Component → Program</strong>.
      </p>

      {/* Entity selectors */}
      <div style={{ background: '#fff', border: '1px solid #dde3ec', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <SearchableSelect
            label="Program"
            value={selectedProgram}
            onChange={v => setSelectedProgram(v)}
            options={programOptionGroups.options}
            closedOptions={programOptionGroups.closedOptions}
            placeholder="— select program —"
          />
          <SearchableSelect
            label="Component"
            value={selectedComponent}
            onChange={v => setSelectedComponent(v)}
            options={componentOptionGroups.options}
            closedOptions={componentOptionGroups.closedOptions}
            placeholder="— select component —"
          />
          <SearchableSelect
            label="Goal"
            value={selectedGoal}
            onChange={v => setSelectedGoal(v)}
            options={goalOptionGroups.options}
            closedOptions={goalOptionGroups.closedOptions}
            placeholder="— select goal —"
          />
          {(selectedProgram || selectedComponent || selectedGoal) && (
            <div style={{ paddingBottom: '2px' }}>
              <div style={labelStyle}>&nbsp;</div>
              <button onClick={clearSelections} style={{
                padding: '6px 14px', border: '1px solid #ccc', borderRadius: '4px',
                background: '#fff', color: '#666', cursor: 'pointer', fontSize: '13px',
              }}>✕ Clear</button>
            </div>
          )}
        </div>

        {level && qaContext && (
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#888' }}>
            Configuring rules at <strong>{LEVEL_LABELS[level]}</strong> level: {qaContext.entity_label}
          </div>
        )}
      </div>

      {/* Rules panel */}
      {!level ? (
        <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>
          Select a program, component, or goal above to configure its QA rules.
        </div>
      ) : loading ? (
        <div style={{ color: '#999', fontSize: '13px' }}>Loading rules…</div>
      ) : qaContext ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: '#34495e' }}>Campaign QA Checks</h3>
            <span style={{ fontSize: '12px', color: '#aaa' }}>Toggle on/off per check · Changes save automatically</span>
          </div>

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', marginBottom: '6px' }}>F2 — REQUIRED FIELDS (Program/Component only)</div>
          {F2_CONFIGURABLE_CHECKS.map(checkName => (
            <CheckRow key={checkName} checkName={checkName} rule={ruleMap[checkName] || null}
              inherited={qaContext.inherited || {}} targetingLists={qaContext.targeting_summary}
              sizeBands={qaContext.employee_size_bands} allLeadFields={allLeadFields}
              defaultFieldSettings={qaContext.default_field_settings || {}}
              defaultDateFormat={qaContext.default_date_format}
              apiDestinations={apiDestinations} apiExportConfigs={apiExportConfigs}
              programName={resolvedProgramName} resolvedProgramId={resolvedProgramId}
              onSave={handleSave} saving={savingCheck === checkName}
              overridesBelow={(qaContext.overrides_below || {})[checkName]}
              level={level} entityId={entityId} onListChange={loadContextSilently} />
          ))}

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', margin: '16px 0 6px' }}>F4 — TARGETING CHECKS</div>
          {F4_CHECKS.map(checkName => (
            <CheckRow key={checkName} checkName={checkName} rule={ruleMap[checkName] || null}
              inherited={qaContext.inherited || {}} targetingLists={qaContext.targeting_summary}
              sizeBands={qaContext.employee_size_bands} allLeadFields={allLeadFields}
              programs={programs} resolvedProgramId={resolvedProgramId}
              domainCapMax={(ruleMap.domain_cap || (qaContext.inherited || {}).domain_cap)?.config_json?.max_per_domain}
              onSave={handleSave} saving={savingCheck === checkName}
              overridesBelow={(qaContext.overrides_below || {})[checkName]}
              level={level} entityId={entityId} onListChange={loadContextSilently} />
          ))}

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', margin: '16px 0 6px' }}>F5 — EXTENDED CHECKS</div>
          {F5_CHECKS.map(checkName => (
            <CheckRow key={checkName} checkName={checkName} rule={ruleMap[checkName] || null}
              inherited={qaContext.inherited || {}} targetingLists={qaContext.targeting_summary}
              sizeBands={qaContext.employee_size_bands} allLeadFields={allLeadFields}
              onSave={handleSave} saving={savingCheck === checkName}
              overridesBelow={(qaContext.overrides_below || {})[checkName]}
              level={level} entityId={entityId} onListChange={loadContextSilently} />
          ))}

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', margin: '16px 0 6px' }}>PROGRAM-LEVEL — SUPPRESSION</div>
          <SuppressionRow
            level={level}
            entityId={entityId}
            assignedLists={qaContext.targeting_summary?.suppression}
            globalLists={qaContext.global_suppression_lists}
            onListChange={loadContextSilently}
          />

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', margin: '16px 0 6px' }}>GLOBAL — DO NOT CALL</div>
          <DncRow globalLists={qaContext.global_dnc_lists} />

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', margin: '16px 0 6px' }}>PROGRAM-LEVEL — UPLOAD BEHAVIOR</div>
          <ForceFullQaRow
            level={level}
            program={programs.find(p => String(p.id) === String(entityId)) || null}
            onSaved={handleProgramSaved}
            showToast={showToast}
          />

          <div style={{ fontSize: '11px', fontWeight: '600', color: '#aaa', letterSpacing: '0.05em', margin: '16px 0 6px' }}>CLONE FROM ANOTHER {(CLONE_LEVEL_LABELS[level] || level).toUpperCase()}</div>
          <CloneQaRulesRow
            level={level}
            entityId={entityId}
            sourceCandidates={
              level === 'program'
                ? programs.map(p => ({ id: p.id, label: p.program_name || `Program ${p.id}` }))
                : level === 'component'
                ? allComponents.map(c => ({ id: c.id, label: c.component_name || `Component ${c.id}` }))
                : allGoals.map(g => ({ id: g.id, label: g.goal_name || `Goal ${g.id}` }))
            }
            onCloned={loadContext}
            showToast={showToast}
          />

          {/* Always-on F2 checks */}
          <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '14px' }}>
            <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '6px', fontWeight: '600', letterSpacing: '0.05em' }}>
              ALWAYS ACTIVE — F2 CHECKS (not configurable)
            </div>
            {['Email Syntax', 'Business Email', 'Duplicate Email', 'ZeroBounce (when run)'].map(name => (
              <span key={name} style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px',
                borderRadius: '12px', background: '#f4f9ff', border: '1px solid #d0e4f7',
                fontSize: '12px', color: '#555', marginRight: '6px', marginBottom: '6px',
              }}>
                <span style={{ color: '#27ae60', fontWeight: '700' }}>✓</span> {name}
                <span style={pillBadge('#c0392b', '#fdf0ee')}>Reject</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', padding: '12px 20px', borderRadius: '6px',
          background: toast.type === 'error' ? '#c0392b' : '#27ae60',
          color: '#fff', fontSize: '13px', fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 9999,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
